import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useCameraRecovery } from '@renderer/pages/View'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import {
  isFramedStripRecording,
  isRecordingPackageType,
  toRecordingType
} from '@renderer/utils/recordingType'
import { waitForValue } from '@renderer/utils/waitForValue'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils, CameraUnavailableError } from '@renderer/utils/cameras/uniCamUtils'
import { resolveConnectArgs } from '@renderer/utils/cameras/cameraSelection'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'
import { Button } from '@renderer/components'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { isDigitalMode } from '@renderer/lib/runtime'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import FrameBasedCapture from './FrameBasedCapture'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import {
  restoreOriginalSettings,
  applyLiveViewBrightness
} from '@renderer/utils/cameras/cameraSettingsManager'
import QRCode from 'qrcode'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { useDesignLayout } from '@renderer/hooks'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'

interface UserContext {
  user?: {
    email: string
  }
  onCameraRecoveryRequest?: (onReconnected: () => void) => void
}

/**
 * Consecutive no-frame capture attempts before the loop gives up.
 *
 * Generous enough to ride out a flaky USB link (each attempt already carries its own
 * internal retries and a full countdown, so this is ~1-2 minutes of trying), but finite.
 * The value matters far less than the fact that a bound exists at all: the loop it guards
 * previously had none and was measured at 1,218 attempts over 4h49m on a paid session.
 */
const MAX_CONSECUTIVE_CAPTURE_FAILURES = 6

/**
 * How long a capture run waits for an owed recorder that has not been built yet.
 *
 * The recorder only exists once Preview has sized its recording canvas. A retake starts
 * shooting 1 s after mount, and on a slow preview the recorder arrived after the shot had
 * begun, so it was never started and the retaken slot had no clip (pkohal / p0kaho). The
 * guest sees the live preview a moment longer before the countdown. Past this the run
 * goes ahead without video rather than holding the guest on a booth whose recorder will
 * never come.
 */
const OWED_RECORDER_WAIT_MS = 3000

const StandardCapturePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const cameraRecoveryContext = useCameraRecovery()
  const {
    setSessionState,
    userPreferences,
    id,
    gallery,
    galleryKey,
    logActivity
  } = useSessionStateStore()
  const { config: globalConfig, getFilter, filters, getFrame, frames } = useConfigStore()
  const {
    isCompact: captureCompact,
    isPortrait: capturePortrait,
    isTiny: captureTiny,
    designW: layoutCanvasW,
    designH: layoutCanvasH,
    isLandscape: captureLayoutLandscape
  } = useDesignLayout()
  const captureThumbPx = captureCompact ? 100 : 200
  const captureMode = globalConfig?.pages.capture?.settings?.mode || 'standard'
  const isSelfPhotoMode = captureMode === 'self-photo'
  const selfPhotoDuration = Math.max(60, globalConfig?.pages.capture?.settings?.selfPhotoDuration ?? 600)

  const config = globalConfig?.pages.capture
  const [capturedImageURL, setCapturedImageURL] = useState<string | null>(null)
  const [prepCountdown, setPrepCountdown] = useState<string>('00:00')
  const standardFrameForSlots =
    getFrame(userPreferences.frameId || globalConfig?.pages.frame?.settings?.frameIds?.[0] || '') ??
    (frames[0] ? getFrame(frames[0]._id) : undefined)
  const uniquePhotoTypes = new Set(
    (standardFrameForSlots?.drawParams?.images ?? [])
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoCount = uniquePhotoTypes.size
  const initialCaptureCountdown = config?.settings.captureCountdown || 3
  const [captureCountdown, setCaptureCountdown] = useState<number>(initialCaptureCountdown)
  const [preparing, setPreparing] = useState(userPreferences.retakeCount === 0)
  const [capturing, setCapturing] = useState(false)
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const [isCapturingWithShutter, setIsCapturingWithShutter] = useState(false)
  const [isCaptureError, setIsCaptureError] = useState(false)
  // Terminal state: capture gave up and there is nothing to hand the customer.
  // Distinct from isCaptureError, which is the transient "retrying" overlay.
  const [captureHardFailure, setCaptureHardFailure] = useState(false)
  const [isRecoveringCamera, setIsRecoveringCamera] = useState(false)
  const [isPreviewPaused, setIsPreviewPaused] = useState(false)
  const [countdownKey, setCountdownKey] = useState(0)
  const isRecoveringRef = useRef(false) // Ref to check recovery state in callbacks
  const previewReadyAfterRecoveryRef = useRef<boolean>(false) // Track preview readiness after recovery
  // Consecutive camera recoveries with NO real captured frame in between. Bounds the
  // recovery loop so a dead DSLR (whose stale Camera object makes tryToConnect resolve
  // instantly) can't recover→resume→re-fail forever. Reset only on a captured frame.
  const consecutiveRecoveryRef = useRef(0)
  // Consecutive capture attempts that produced no frame, counted regardless of WHY.
  //
  // This is the backstop that `consecutiveRecoveryRef` cannot be: that one lives inside
  // handleCameraRecovery(), which is only reached when the hardware-failure predicate
  // fires. When the predicate misses (it used to require shutter mode, while the default
  // liveview path is shutter-mode OFF) nothing counted failures at all, and the loop —
  // whose only decrement happens on SUCCESS — retried forever. Observed: 1,218 failed
  // captures over 4h49m on a paid session that produced no print and no gallery.
  // Incremented on every failed attempt, reset only on a real captured frame.
  const consecutiveCaptureFailuresRef = useRef(0)
  // Set once the loop has given up, so the failure path stops re-arming the countdown.
  const captureAbortedRef = useRef(false)

  const prepCountdownHook = useCountdown(
    userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
  )
  const captureCountdownHook = useCountdown(config?.settings.captureCountdown || 3, { audioCue: true })

  const stopCaptureRef = useRef<boolean>(false)

  // FIX 1: Add capture lock to prevent concurrent captures
  const captureLockRef = useRef<boolean>(false)

  // FIX 2: Track captured file hashes to prevent duplicate uploads
  const capturedFilesRef = useRef<Set<string>>(new Set())

  const captureCount = isSelfPhotoMode
    ? 1
    : Math.max(uniquePhotoCount, config?.settings.captureCount || 1)
  const remainingCapturesRef = useRef<number>(
    userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
  )
  const phoneTriggerRequestedRef = useRef<boolean>(false)
  const resolveWaitRef = useRef<(() => void) | null>(null)
  const filterSettings = globalConfig?.pages.filter?.settings
  const applyAfterCapture = filterSettings?.applyAfterCapture ?? false
  const filterPageEnabled = filterSettings?.enabled ?? true
  const selectedFilter = userPreferences.filterId
    ? getFilter(userPreferences.filterId) ?? (filterPageEnabled ? filters[0] : undefined)
    : filterPageEnabled
      ? filters[0]
      : undefined
  const {
    downloadLocal: downloadLocalSetting,
    cameraSettings,
    selectedCamera: selectedCameraId
  } = useSettingsStore()
  // Public/digital web booths never save captures to the visitor's disk. The
  // download flag is a kiosk/operator setting (defaults ON) with no meaning on a
  // shared web booth, so force it off in digital — this also neutralises the
  // recording-download uses below. Kiosk is unchanged (isDigitalMode() is false).
  const downloadLocal = downloadLocalSetting && !isDigitalMode()
  const { fetchSubscriptionAndCredits } = useCreditsStore()
  const sessionReset = useSessionReset()

  // Frame-based mode state
  const {
    remainingTime: sharedRemainingTime,
    isExpired: isSharedCountdownExpired,
    startCountdown: startSharedCountdown
  } = useSharedCountdown()
  const selectedFrame = getFrame(userPreferences.frameId || '')
  const [currentPhotoTypeIndex, setCurrentPhotoTypeIndex] = useState<number>(0)
  const [capturedPhotosByType, setCapturedPhotosByType] = useState<Map<string, string>>(new Map())
  const isRetakeFromRetakePage = userPreferences.isRetakeFromRetakePage || false

  // Get unique photo types from frame for frame-based mode
  const frameBasedUniquePhotoTypes =
    captureMode === 'frame-based' && selectedFrame
      ? new Set(
          selectedFrame.drawParams.images
            .map((image) => image.type)
            .filter((type) => type.startsWith('photo-'))
        )
      : new Set<string>()
  const frameBasedUniquePhotoTypesArray = Array.from(frameBasedUniquePhotoTypes).sort()
  const frameBasedPhotoSlots =
    captureMode === 'frame-based' && selectedFrame
      ? selectedFrame.drawParams.images.filter((img) => img.type.startsWith('photo-'))
      : []
  const frameBasedCaptureCount = captureMode === 'frame-based' ? Math.max(frameBasedUniquePhotoTypes.size, 1) : 0

  // Delegate to shared helper for consistent Number.isFinite + positive guard across all pages.
  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  const effectiveCaptureRatio = userPreferences.framePhotoRatio || getNormalizedCaptureRatio()
  const displayRatio = effectiveCaptureRatio || 4 / 3
  /** Preview L3/L2 mask only — omit when captureRatio is auto and no frame (no phantom 4:3 overlay). */
  const previewHighlightRatio =
    userPreferences.framePhotoRatio ?? getNormalizedCaptureRatio() ?? undefined

  const useRecording = userPreferences.useRecording
  const recordingSpeed = globalConfig?.pages.capture?.settings?.recordingSpeed || 1
  const FPS = 30

  // Initialize recorders after canvas is ready
  const sessionCanvasRecorderRef = useRef<Recorder | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)
  /** Dimensions the current recorder was encoded with. Used to detect drift before start(). */
  const recorderEncoderDimsRef = useRef<{ w: number; h: number } | null>(null)

  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  /** Preview hidden 2D buffer: rotation + mirror baked for WebCodecs (matches on-screen preview). */
  const recordingCanvasElRef = useRef<HTMLCanvasElement | null>(null)
  const [recordingEncoderDims, setRecordingEncoderDims] = useState<{
    width: number
    height: number
  } | null>(null)
  const onRecordingCanvasReady = useCallback(
    (el: HTMLCanvasElement, _ctx: CanvasRenderingContext2D, dim: { width: number; height: number }) => {
      recordingCanvasElRef.current = el
      setRecordingEncoderDims({ width: dim.width, height: dim.height })
    },
    []
  )
  const [selfPhotoQrDataUrl, setSelfPhotoQrDataUrl] = useState<string>('')
  const [selfPhotoTriggerUrlDisplay, setSelfPhotoTriggerUrlDisplay] = useState<string>('')
  // Self-photo runtime controls (end-customer)
  const [selfPhotoCountdownSeconds, setSelfPhotoCountdownSeconds] = useState<number>(6)
  const selfPhotoCountdownSecondsRef = useRef(6)
  const [selfPhotoAutoLoop, setSelfPhotoAutoLoop] = useState<boolean>(false)
  const selfPhotoAutoLoopRef = useRef(false)
  const [sessionTimerSeconds, setSessionTimerSeconds] = useState<number>(0)
  const sessionTimerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [showEndSessionModal, setShowEndSessionModal] = useState<boolean>(false)

  useEffect(() => {
    selfPhotoCountdownSecondsRef.current = selfPhotoCountdownSeconds
  }, [selfPhotoCountdownSeconds])
  useEffect(() => {
    selfPhotoAutoLoopRef.current = selfPhotoAutoLoop
  }, [selfPhotoAutoLoop])

  useEffect(() => {
    const rec = recordingCanvasElRef.current
    const recCtx = rec?.getContext('2d')
    if (!recCtx || !rec || !recordingEncoderDims || recordingEncoderDims.width <= 0) {
      // Not an error here: this effect runs before the recording canvas has mounted
      // and sized itself, so an early bail is normal. What is NOT normal is still
      // having no recorder once capture begins — startCaptureSession reports that.
      return
    }

    const bw = rec.width
    const bh = rec.height
    const evenW = bw > 0 ? Math.floor(bw / 2) * 2 || 2 : Math.floor(recordingEncoderDims.width / 2) * 2 || 2
    const evenH = bh > 0 ? Math.floor(bh / 2) * 2 || 2 : Math.floor(recordingEncoderDims.height / 2) * 2 || 2

    if (bw > 0 && bh > 0 && (evenW !== Math.floor(bw / 2) * 2 || evenH !== Math.floor(bh / 2) * 2)) {
      console.warn(
        `[Capture] recorder dims (${evenW}x${evenH}) diverge from recording canvas (${bw}x${bh})`
      )
    }

    recorderEncoderDimsRef.current = { w: evenW, h: evenH }

    // Normalised so a legacy `'recording'` package still gets a recorder. This effect is
    // the point of no return: without a recorder here, every shot's clip is simply never
    // recorded and no later stage can recover it.
    const effectiveRecordingType = toRecordingType(userPreferences.recordingType)

    if (effectiveRecordingType === 'session_recording') {
      const newSessionRecorder = new Recorder(recCtx as unknown as RenderingContext, {
        name: `dreambooth-${id}-session`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          width: evenW,
          height: evenH,
          bitrate: 2000000,
          latencyMode: 'realtime'
        },
        duration: Infinity,
        download: downloadLocal && effectiveRecordingType === 'session_recording',
        frameRate: FPS
      })
      sessionCanvasRecorderRef.current = newSessionRecorder
    }

    if (isFramedStripRecording(effectiveRecordingType)) {
      const newCaptureRecorder = new Recorder(recCtx as unknown as RenderingContext, {
        name: `dreambooth-${id}-capture`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          width: evenW,
          height: evenH,
          bitrate: 2000000,
          latencyMode: 'realtime'
        },
        duration: Infinity,
        download: downloadLocal && isFramedStripRecording(effectiveRecordingType),
        frameRate: FPS
      })
      captureCanvasRecorderRef.current = newCaptureRecorder
    }

    return (): void => {
      sessionCanvasRecorderRef.current?.stop()
      captureCanvasRecorderRef.current?.stop()
    }
  }, [recordingEncoderDims, id, downloadLocal, userPreferences.recordingType, FPS])

  // Load image and get its ratio when capturedImageURL changes
  useEffect(() => {
    if (capturedImageURL) {
      const img = new Image()
      img.onload = (): void => {
        setSourceRatio(img.width / img.height)
      }
      img.src = capturedImageURL
    }
  }, [capturedImageURL])

  // Self Photo Mode: register session with main process, POST trigger-info to Next.js, generate QR, subscribe to trigger
  useEffect(() => {
    if (!isSelfPhotoMode || !id || !projectId) return
    if (typeof window === 'undefined' || !window.api?.registerSelfPhotoSession) return

    setSessionState({ gallery: { capturedItems: [] } })

    const cleanupRef = { current: null as (() => void) | null }
    const apiBase = process.env.VITE_API_URL || ''
    const triggerPath = `${apiBase}/id/trigger/${id}`.replace(/([^:]\/)\/+/g, '$1/')
    QRCode.toDataURL(triggerPath, { width: 160, margin: 1 })
      .then((qrData) => {
        setSelfPhotoQrDataUrl(qrData)
        setSelfPhotoTriggerUrlDisplay(triggerPath.replace(/^https?:\/\/(www\.)?/i, ''))
      })
      .catch((e) => console.warn('[SelfPhoto] QR generate failed', e))

    const register = async (): Promise<void> => {
      let unsubTrigger: (() => void) | undefined
      let unsubSettings: (() => void) | undefined
      try {
        const { tunnelUrl } = await window.api.registerSelfPhotoSession({
          sessionId: id,
          projectId,
          countdownSeconds: selfPhotoCountdownSecondsRef.current,
          autoLoop: selfPhotoAutoLoopRef.current
        })
        if (tunnelUrl) {
          const res = await fetch(`${apiBase}/api/sessions/trigger-info`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              sessionId: id,
              projectId,
              tunnelUrl
            })
          })
          if (!res.ok) console.warn('[SelfPhoto] Failed to register trigger-info with backend')
        }
        if (typeof window.api.onSelfPhotoTrigger === 'function') {
          unsubTrigger = window.api.onSelfPhotoTrigger((sid: string, settings?: { countdownSeconds?: number; autoLoop?: boolean }) => {
            if (sid === id) {
              if (settings) {
                if (typeof settings.countdownSeconds === 'number' && [0, 3, 6, 10].includes(settings.countdownSeconds)) {
                  selfPhotoCountdownSecondsRef.current = settings.countdownSeconds
                  setSelfPhotoCountdownSeconds(settings.countdownSeconds)
                  if (settings.countdownSeconds === 0) {
                    selfPhotoAutoLoopRef.current = false
                    setSelfPhotoAutoLoop(false)
                  }
                }
                if (typeof settings.autoLoop === 'boolean') {
                  selfPhotoAutoLoopRef.current = settings.autoLoop
                  setSelfPhotoAutoLoop(settings.autoLoop)
                }
              }
              phoneTriggerRequestedRef.current = true
              const resolve = resolveWaitRef.current
              if (resolve) {
                resolveWaitRef.current = null
                resolve()
              }
            }
          })
        }
        if (typeof window.api.onSelfPhotoSettingsFromRemote === 'function') {
          unsubSettings = window.api.onSelfPhotoSettingsFromRemote((settings: { countdownSeconds?: number; autoLoop?: boolean }) => {
            if (typeof settings.countdownSeconds === 'number' && [0, 3, 6, 10].includes(settings.countdownSeconds)) {
              selfPhotoCountdownSecondsRef.current = settings.countdownSeconds
              setSelfPhotoCountdownSeconds(settings.countdownSeconds)
              if (settings.countdownSeconds === 0) {
                selfPhotoAutoLoopRef.current = false
                setSelfPhotoAutoLoop(false)
              }
            }
            if (typeof settings.autoLoop === 'boolean') {
              selfPhotoAutoLoopRef.current = settings.autoLoop
              setSelfPhotoAutoLoop(settings.autoLoop)
            }
          })
        }
      } catch (e) {
        console.warn('[SelfPhoto] Register failed:', e)
      }
      cleanupRef.current = (): void => {
        unsubTrigger?.()
        unsubSettings?.()
        if (window.api?.endSelfPhotoSession) window.api.endSelfPhotoSession()
      }
    }
    register()
    return (): void => {
      cleanupRef.current?.()
    }
  }, [isSelfPhotoMode, id, projectId])

  // Calculate image style based on source and display ratios
  const getImageStyle = (): React.CSSProperties => {
    if (!sourceRatio) return { visibility: 'hidden' as const }

    return {
      width: sourceRatio > displayRatio ? `${(sourceRatio / displayRatio) * 100}%` : '100%',
      height: sourceRatio > displayRatio ? '100%' : `${(displayRatio / sourceRatio) * 100}%`,
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }
  }

  // Container style maintains the display ratio
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: `${captureThumbPx}px`,
    aspectRatio: `${displayRatio}`,
    overflow: 'hidden'
  }

  useEffect(() => {
    // Frame-based mode setup
    if (captureMode === 'frame-based') {
      // Set up prep countdown callbacks for frame-based mode
      prepCountdownHook.setOnTick(() => {
        setPrepCountdown(prepCountdownHook.getFormattedRemainingSeconds())
      })

      prepCountdownHook.setOnComplete(() => {
        startFrameBasedCaptureSession()
      })

      // Set up capture countdown callbacks for frame-based mode
      captureCountdownHook.setOnTick(() => {
        setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
      })

      captureCountdownHook.setOnComplete(() => {
        handleFrameBasedCapture()
      })

      // If coming from retake page, skip prep and start capture immediately
      if (isRetakeFromRetakePage) {
        startFrameBasedCaptureSession()
      } else {
        // Start prep countdown on mount
        prepCountdownHook.start()
      }

      return (): void => {
        prepCountdownHook.stop()
        captureCountdownHook.stop()
        stopCaptureRef.current = true
      }
    }

    // Standard mode setup (existing logic)
    // Set up prep countdown callbacks
    prepCountdownHook.setOnTick(() => {
      setPrepCountdown(prepCountdownHook.getFormattedRemainingSeconds())
    })

    prepCountdownHook.setOnComplete(() => {
      startCaptureSession()
    })

    // Set up capture countdown callbacks
    captureCountdownHook.setOnTick(() => {
      // Don't update if recovery is in progress
      if (isRecoveringRef.current) {
        return
      }
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      // Don't capture if recovery is in progress
      if (isRecoveringRef.current) {
        console.log('[COUNTDOWN] Recovery in progress, skipping capture from global callback')
        return
      }
      handleCapture()
    })

    // Handle auto-start when prepTimeout is null/0 (not a retake)
    const prepTimeout = config?.settings.prepTimeout
    const isRetake = userPreferences.retakeCount > 0
    if ((prepTimeout === null || prepTimeout === 0 || prepTimeout === undefined) && !isRetake) {
      // Auto-start immediately - this will trigger credit deduction in startCaptureSession()
      startCaptureSession()
    } else {
      prepCountdownHook.start()
    }

    return (): void => {
      sessionCanvasRecorderRef.current?.stop()
      captureCanvasRecorderRef.current?.stop()
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true
      if (sessionTimerIntervalRef.current) {
        clearInterval(sessionTimerIntervalRef.current)
        sessionTimerIntervalRef.current = null
      }
      capturedFilesRef.current.clear()
    }
  }, [])

  // Frame-based mode: Update countdown callbacks when currentPhotoTypeIndex changes
  useEffect(() => {
    if (captureMode !== 'frame-based') return

    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      handleFrameBasedCapture()
    })
  }, [captureMode, currentPhotoTypeIndex])

  // Frame-based mode initialization and shared countdown
  useEffect(() => {
    if (captureMode !== 'frame-based') return

    // Start shared countdown if not already started
    if (!useSessionStateStore.getState().userPreferences.sharedCountdownStartTime) {
      startSharedCountdown()
      logActivity('Shared countdown started on Capture page (frame-based mode)', {
        duration: config?.settings.captureTimeout || 600
      })
    }

    // Clear retake flag if it was set
    if (isRetakeFromRetakePage) {
      setSessionState({
        userPreferences: {
          isRetakeFromRetakePage: false
        }
      })
    }

    // Navigate away if countdown expired
    if (isSharedCountdownExpired) {
      logActivity('Shared countdown expired on Capture page (frame-based mode)')
      if (capturedPhotosByType.size > 0) {
        navigate(`/${projectId}/retake`)
      } else {
        navigate(`/${projectId}/frame`)
      }
    }
  }, [captureMode, isSharedCountdownExpired, capturedPhotosByType.size])

  // Monitor preview readiness after recovery
  useEffect(() => {
    if (isRecoveringRef.current && canvasDimensions) {
      console.log('[RECOVERY] Preview is ready after recovery')
      previewReadyAfterRecoveryRef.current = true
    }
  }, [canvasDimensions])

  const startCaptureSession = async (): Promise<void> => {
    const prepTime = userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
    const captureTime = isSelfPhotoMode ? selfPhotoCountdownSecondsRef.current : (config?.settings.captureCountdown || 3)
    stopCaptureRef.current = false // Reset the stopCaptureRef
    // Fresh session: clear the abort latch and failure streak from any previous attempt,
    // so a recovered booth is not permanently poisoned by an earlier camera outage.
    captureAbortedRef.current = false
    consecutiveCaptureFailuresRef.current = 0
    setCaptureHardFailure(false)
    if (isSelfPhotoMode) {
      setSessionTimerSeconds(selfPhotoDuration)
      sessionTimerIntervalRef.current = setInterval(() => {
        setSessionTimerSeconds((prev) => {
          if (prev <= 1) {
            if (sessionTimerIntervalRef.current) {
              clearInterval(sessionTimerIntervalRef.current)
              sessionTimerIntervalRef.current = null
            }
            stopCaptureRef.current = true
            const resolve = resolveWaitRef.current
            if (resolve) {
              resolveWaitRef.current = null
              resolve()
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    
    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        try {
          await Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ])
        } catch (error) {
          // If fetch fails or times out, log but don't block session start
          console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
        }
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        stateUploader.uploadSessionState()
      }, 2000)
    }

    // Log processing start
    logActivity('Capture session started!', {
      isRetake: userPreferences.retakeCount > 0,
      useRecording: useRecording,
      captureTime,
      prepTime
    })

    try {
      prepCountdownHook.stop()

      // A missing recorder here means this capture run produces no video, and every later
      // stage tests the ref before doing anything, so the loss leaves no trace unless it is
      // logged now (sessions uqd410 / hz6byb: paid, recorded nothing, logged a clean run).
      //
      // It can also just be LATE. The recorder is built once Preview has sized its
      // recording canvas, and a retake starts shooting 1 s after mount: on a slow preview
      // the recorder appeared after the shot began, was never started, and the retaken
      // slot had no clip (pkohal / p0kaho, 5 of 189 retakes). So wait a bounded moment
      // for it before the countdown starts, and only log it as missing after that.
      //
      // Gated on a recording actually being OWED, not on `useRecording` alone: that flag
      // starts `true` in the store and Welcome copies the project's `recordingEnabled`
      // (default `true`) into it, so ~835 capture runs a week have it set with no
      // recording sold. Owed = a recording type is configured, or one was bought.
      const effectiveRecordingType = toRecordingType(userPreferences.recordingType)
      const recordingPurchased = (
        useSessionStateStore.getState().transaction.selectedPackages ?? []
      ).some((pkg) => isRecordingPackageType(pkg.type))
      if (useRecording && !isSelfPhotoMode && (effectiveRecordingType || recordingPurchased)) {
        const owedRecorderRef =
          effectiveRecordingType === 'session_recording'
            ? sessionCanvasRecorderRef
            : captureCanvasRecorderRef
        // Without a recording type no recorder is ever built, so there is nothing to wait for.
        const { value: owedRecorder, waitedMs } = effectiveRecordingType
          ? await waitForValue(() => owedRecorderRef.current, {
              timeoutMs: OWED_RECORDER_WAIT_MS,
              shouldAbort: () => stopCaptureRef.current
            })
          : { value: owedRecorderRef.current, waitedMs: 0 }
        if (stopCaptureRef.current) return
        if (!owedRecorder) {
          const recEl = recordingCanvasElRef.current
          logActivity('No recorder for an owed recording — this capture run will have no video', {
            recordingType: userPreferences.recordingType ?? null,
            recordingPurchased,
            isRetake: userPreferences.retakeCount > 0,
            hasRecordingCanvas: Boolean(recEl),
            encoderDims: recorderEncoderDimsRef.current ?? null,
            waitedMs,
            component: 'capture_recorder',
            level: 'error'
          })
        } else if (waitedMs > 0) {
          // Field confirmation of the retake race fix: each line is a clip that used to be lost.
          logActivity('Recorder arrived late — waited before starting capture', {
            recordingType: userPreferences.recordingType ?? null,
            isRetake: userPreferences.retakeCount > 0,
            waitedMs,
            component: 'capture_recorder'
          })
        }
      }

      setPreparing(false)
      setCapturing(true)

      // Start session recording if enabled
      if (useRecording && sessionCanvasRecorderRef.current) {
        // Drift guard: warn if the canvas backing store changed since the recorder was created.
        // This would cause the encoder to receive bitmaps at a different size than its configured
        // width/height, producing stretched or green-bar video.
        const recEl =
          recordingCanvasElRef.current ||
          (document.querySelector('canvas[data-preview-type="camera-recording"]') as HTMLCanvasElement | null)
        const encDims = recorderEncoderDimsRef.current
        if (recEl && encDims && (recEl.width !== encDims.w || recEl.height !== encDims.h)) {
          console.warn(
            `[Capture] session recorder encoder (${encDims.w}x${encDims.h}) diverged from recording canvas (${recEl.width}x${recEl.height}) before start`
          )
        }
        try {
          await sessionCanvasRecorderRef.current.start()
        } catch (error) {
          cogoToast.error('Error starting video recorder')
          console.error('Error starting session recorder', error)
          // Log recording failure
          const errorMessage =
            error instanceof Error
              ? error.message
              : t('capture.errorStartingSessionRecorder', 'Error starting session recorder')
          logActivity(`Failed to start session recorder: ${errorMessage}`, {
            error: errorMessage,
            component: 'session_recorder',
            level: 'error'
          })
        }
      }

      // Log processing complete
      runCaptureLoop()
    } catch (error) {
      console.error('Error in startCaptureSession:', error)
      // Log processing failure
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Error in startCaptureSession: ${errorMessage}`, {
        error: errorMessage,
        isRetake: userPreferences.retakeCount > 0,
        level: 'error'
      })
    }
  }

  const stopRecording = async (): Promise<void> => {
    if (useRecording && sessionCanvasRecorderRef.current) {
      const recordedChunks = await sessionCanvasRecorderRef.current.stop()

      const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
      const mp4Url = URL.createObjectURL(mp4Blob)

      const videoElement = document.createElement('video')
      videoElement.src = mp4Url
      videoElement.controls = true

      const videoFile = new File([mp4Blob], `dreambooth-${id}-session.mp4`, { type: 'video/mp4' })

      // Start video processing in background
      if (userPreferences.recordingType === 'session_recording') {
        backgroundVideoProcessor.processVideo({
          id: `session-${id}-${Date.now()}`,
          type: 'session',
          resources: [], // Not needed for capture videos
          frameConfig: {
            id: 'capture-video',
            baseImage: '',
            drawParams: {
              canvasWidth: canvasDimensions?.width || 1200,
              canvasHeight: canvasDimensions?.height || 1800,
              backgroundColor: '#FFFFFF',
              images: []
            }
          },
          sessionId: id || '',
          userId: context?.user?.email || '',
          projectId: projectId || '',
          galleryId: galleryKey || gallery.id,
          videoFile, // Pass the video file for capture videos
          onComplete: (url) => {
            // Update state with video URL when complete
            const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
            logActivity('Session recording uploaded.', {
              type: 'recording',
              url
            })
            setSessionState({
              gallery: {
                capturedItems: [
                  ...prevItems,
                  {
                    id: 'dreambooth-session-recording-' + new Date().getTime(),
                    recordingUrl: mp4Url
                  }
                ]
              }
            })
          },
          onError: (error) => {
            console.error('Error processing session video:', error)
            cogoToast.error(
              t('capture.errorProcessingSessionVideo', 'Error processing session video')
            )
          }
        })
      }
    }
  }

  /**
   * End a capture session that cannot produce frames any more.
   *
   * The priority order is: keep whatever the customer already paid for, then make the
   * failure visible to a human. Never silently return to Welcome with money taken and
   * nothing delivered, and never keep retrying — both were observed in production.
   *
   * Returns 'deliver' when the caller should break out of the loop and run the normal
   * completion path (upload + retake/filter navigation), or 'stop' when this function has
   * already taken over the session and the caller must return immediately.
   */
  const abortCaptureAfterRepeatedFailures = async (): Promise<'deliver' | 'stop'> => {
    // Latch first so any in-flight handleCapture stops doing work and re-arming recovery.
    captureAbortedRef.current = true

    captureCountdownHook.stop()
    captureCountdownHook.setOnComplete(null)
    setCapturing(false)
    setIsCapturingWithShutter(false)
    setIsCaptureError(false)

    const isPaidSession = useSessionStateStore.getState().transaction?.status === 'settlement'
    const capturedCount = capturedFilesRef.current.size

    console.error(
      `[CAPTURE LOOP] Aborting after ${consecutiveCaptureFailuresRef.current} consecutive failed captures ` +
        `(paid=${isPaidSession}, captured=${capturedCount})`
    )
    // Record the classified cause (focus / usb / unknown) so the abort is diagnosable —
    // the cap fires on repeated no-frame attempts whatever produced them.
    const lastCaptureError = unifiedCameraUtils.getLastCaptureError('primary')
    logActivity('Capture aborted after repeated failures - no frames produced', {
      errorKind: lastCaptureError?.kind ?? 'unknown',
      lastError: lastCaptureError?.message ?? 'No image data captured',
      error: 'No image data captured',
      consecutiveFailures: consecutiveCaptureFailuresRef.current,
      capturedSoFar: capturedCount,
      remainingCaptures: remainingCapturesRef.current,
      isPaidSession,
      isRetake: userPreferences.retakeCount > 0,
      level: 'error'
    })

    // Some photos DID land — deliver them. A short strip beats nothing at all. Hand back
    // to the normal completion path rather than navigating here, so the session still
    // gets its recorder shutdown, state upload and the real retake/filter routing rules
    // (retake may be disabled or its per-session limit reached).
    // NOTE: stopCaptureRef is deliberately NOT set here — the post-loop code treats it as
    // "operator quit" and returns before uploading.
    if (capturedCount > 0) {
      cogoToast.error(
        t(
          'capture.abortedPartial',
          'We could not take the remaining photos. Continuing with the ones already taken.'
        ),
        { hideAfter: 6 }
      )
      return 'deliver'
    }

    // Nothing was captured: this session is over. Stop the loop for real.
    stopCaptureRef.current = true

    if (useRecording && sessionCanvasRecorderRef.current) {
      try {
        await stopRecording()
      } catch (error) {
        console.error('Error stopping session recording during abort', error)
      }
    }

    // A paid session must NOT be dumped back to Welcome — that is the "paid and got
    // nothing, with no trace" path. Hold on a terminal operator screen so staff can
    // refund or restart deliberately.
    if (isPaidSession) {
      setCaptureHardFailure(true)
      cogoToast.error(
        t(
          'capture.abortedPaid',
          'We could not take your photos. Your session is saved — please ask an operator for help.'
        ),
        { hideAfter: 0 }
      )
      return 'stop'
    }

    cogoToast.error(
      t('capture.abortedUnpaid', 'We could not take your photos. Returning to the home page.'),
      { hideAfter: 6 }
    )
    await sessionReset('capture_repeated_failure')
    return 'stop'
  }

  const runCaptureLoop = async (): Promise<void> => {
    console.log('Starting capture loop with remaining captures:', remainingCapturesRef.current)
    // Background photo-persistence tasks. Each shot's transforms/hashing/gallery write run
    // off the countdown's critical path; we drain them before navigating away so the next
    // page always sees the full set of captured photos.
    const pendingProcessing: Promise<unknown>[] = []
    while (
      (isSelfPhotoMode || remainingCapturesRef.current > 0) &&
      !stopCaptureRef.current
    ) {
      // Hard backstop: the loop only ever decrements on SUCCESS, so without this a
      // permanently failing camera spins here forever — the customer pays, watches
      // "Retrying in a moment…", and leaves with no print and no gallery because
      // uploadSessionState() and navigate() both sit AFTER this loop.
      //
      // Deliberately independent of deviceType, capture mode, and whether recovery ever
      // ran: every previous guard was conditional and every one of them missed. This one
      // counts attempts that produced no frame and stops, whatever the reason.
      if (consecutiveCaptureFailuresRef.current >= MAX_CONSECUTIVE_CAPTURE_FAILURES) {
        const outcome = await abortCaptureAfterRepeatedFailures()
        if (outcome === 'stop') return
        // 'deliver': some photos were captured — fall through to the normal completion
        // path below so they are uploaded and the customer still gets a strip.
        break
      }

      // Wait for camera recovery to complete if in progress
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Waiting for camera recovery to complete...')
        const recoveryStartTime = Date.now()

        // A PAID session must never be silently dumped back to Welcome on a camera hiccup —
        // that loses a session the customer already paid for (the observed "paid then back to
        // welcome" path). Hold indefinitely while the recovery CameraPrompt stays up so an
        // operator can reconnect and resume; only an explicit quit (stopCaptureRef, via the
        // Exit flow -> unmount) ends it. UNPAID sessions keep the original 2-minute -> home.
        // Ported from bahela/Capture.tsx (commit f2149a1) — the standard `view` flow never got it.
        const isPaidSession =
          useSessionStateStore.getState().transaction?.status === 'settlement'
        const maxRecoveryWait = isPaidSession ? Number.POSITIVE_INFINITY : 120000
        let notifiedPaidHold = false

        while (
          (isRecoveringRef.current || isRecoveringCamera) &&
          !stopCaptureRef.current &&
          Date.now() - recoveryStartTime < maxRecoveryWait
        ) {
          // Once the wait drags on for a paid session, surface a clear, non-dismissing operator
          // message so the booth isn't silently stuck with no explanation.
          if (isPaidSession && !notifiedPaidHold && Date.now() - recoveryStartTime > 120000) {
            notifiedPaidHold = true
            cogoToast.error(
              t(
                'capture.recoveryHoldPaid',
                'Camera issue — your paid session is saved. Please ask an operator for help.'
              ),
              { hideAfter: 0 }
            )
            logActivity('Camera recovery stalled on PAID session - holding for operator', {
              remainingCaptures: remainingCapturesRef.current,
              level: 'error'
            })
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
        }

        // Explicit quit during recovery — stop cleanly (the Exit flow owns session reset + nav).
        if (stopCaptureRef.current) {
          return
        }

        if (isRecoveringRef.current || isRecoveringCamera) {
          // Only reachable for UNPAID sessions (paid sessions wait indefinitely above).
          console.error('[CAPTURE LOOP] Camera recovery timeout after 2 minutes')
          cogoToast.error(
            t('capture.recoveryTimeout', 'Camera recovery timed out. Returning to home page.')
          )
          logActivity('Camera recovery timeout - navigating home', {
            remainingCaptures: remainingCapturesRef.current,
            level: 'error'
          })
          // P1-3: reset the session on abort. Plain navigate('/' + projectId)
          // left the half-captured session in memory (sessionId, partial
          // gallery, accumulated logs) so the next Welcome → startSession
          // inherited that state until cleanupSession ran. sessionReset()
          // flushes upload, revokes URLs, cleans up, then navigates.
          await sessionReset('camera_recovery_timeout')
          return
        }

        // Recovery completed, wait for preview to be ready
        console.log('[CAPTURE LOOP] Recovery completed, waiting for preview readiness...')
        const previewReadyStartTime = Date.now()
        const maxPreviewWait = 3000 // 3 seconds max for preview

        while (
          !previewReadyAfterRecoveryRef.current &&
          Date.now() - previewReadyStartTime < maxPreviewWait
        ) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        if (!previewReadyAfterRecoveryRef.current) {
          console.warn('[CAPTURE LOOP] Preview readiness timeout, continuing anyway')
        }

        // Add 1 second delay for UX (let user see preview is back)
        console.log('[CAPTURE LOOP] Preview ready, adding 1s delay for UX')
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Reset preview ready flag for next potential recovery
        previewReadyAfterRecoveryRef.current = false
      }

      let frameCount = 0
      const startTime = performance.now()
      const captureId = id + '-' + Date.now()

      console.log('Starting new capture iteration:', {
        remainingCaptures: remainingCapturesRef.current,
        useRecording,
        hasRecorder: !!captureCanvasRecorderRef.current,
        isRecovering: isRecoveringCamera
      })

      // Start capture recording if enabled (skip per-capture recording in self-photo mode)
      if (useRecording && captureCanvasRecorderRef.current && !isSelfPhotoMode) {
        // Drift guard: check encoder dims vs live canvas before starting.
        const recEl2 =
          recordingCanvasElRef.current ||
          (document.querySelector('canvas[data-preview-type="camera-recording"]') as HTMLCanvasElement | null)
        const encDims2 = recorderEncoderDimsRef.current
        if (recEl2 && encDims2 && (recEl2.width !== encDims2.w || recEl2.height !== encDims2.h)) {
          console.warn(
            `[Capture] capture recorder encoder (${encDims2.w}x${encDims2.h}) diverged from recording canvas (${recEl2.width}x${recEl2.height}) before start`
          )
        }
        try {
          await captureCanvasRecorderRef.current.start()
          console.log('Successfully started capture recorder')
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error)
          cogoToast.error('Error starting video recorder')
          console.error('Error starting capture recorder', error)
          logActivity(`Failed to start capture recorder: ${errorMessage}`, {
            error: errorMessage,
            recordingType: userPreferences.recordingType,
            remainingCaptures: remainingCapturesRef.current,
            component: 'capture_recorder',
            level: 'error'
          })
        }
      }

      // Check if recovery started before starting countdown
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Recovery in progress, skipping this capture iteration')
        continue
      }

      // Start the countdown and wait for capture
      console.log('Attempting to start countdown')

      // Reset countdown properly before starting (self-photo uses runtime picker value)
      const captureTime = isSelfPhotoMode ? selfPhotoCountdownSecondsRef.current : (config?.settings.captureCountdown || 3)
      captureCountdownHook.stop()
      captureCountdownHook.setRemainingSeconds(captureTime)
      setCaptureCountdown(captureTime)
      setCountdownKey((prev) => prev + 1)

      // Restore onTick every iteration — recovery may have cleared it
      captureCountdownHook.setOnTick(() => {
        if (isRecoveringRef.current) return
        setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
      })

      // Raw-capture gate: resolves the instant the shutter/preview read finishes so the
      // next countdown can begin. The processed File resolves later via processingPromise,
      // and the gallery write happens in the background (pendingProcessing). We only ever
      // block the loop on the raw capture itself.
      // Register onComplete BEFORE start() so that 0s mode (synchronous fire) uses the correct callback
      let processingPromise: Promise<string | undefined> | undefined
      const rawCapturePromise = new Promise<void>((resolveRaw) => {
        captureCountdownHook.setOnComplete(() => {
          if (isRecoveringRef.current || isRecoveringCamera) {
            console.log('[CAPTURE LOOP] Recovery in progress, skipping capture')
            resolveRaw()
            return
          }
          processingPromise = handleCapture(() => {
            // Raw frame captured — camera is free. Count the shot and release the gate so
            // the loop can move on to the next countdown. Clear the "stay still" shutter
            // overlay now (the photo is taken) so it doesn't cover the next countdown while
            // transforms finish in the background.
            setIsCapturingWithShutter(false)
            // A real frame landed — recovery (if any) made genuine progress. Reset the
            // loop-breaker here, on a captured frame, NOT in onCameraReconnected: the bug
            // is precisely reconnect-succeeds→next-capture-fails, so a reconnect is not progress.
            consecutiveRecoveryRef.current = 0
            // A real frame landed — the failure streak is broken.
            consecutiveCaptureFailuresRef.current = 0
            if (!isSelfPhotoMode) remainingCapturesRef.current--
            resolveRaw()
          })
          // Failsafe: release the gate even if the raw capture never signalled
          // (e.g. capture failure or camera recovery), so the loop never hangs.
          processingPromise.finally(() => resolveRaw())
        })
      })

      // Set capturing state to true so countdown is visible
      setCapturing(true)
      // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
      unifiedCameraUtils.prefocus()
      captureCountdownHook.start()
      // Check again immediately after starting (recovery might have started)
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Recovery started right after countdown start, stopping')
        captureCountdownHook.stop()
        captureCountdownHook.setOnComplete(null)
        setCapturing(false)
        continue
      }

      const stepInterval = 1000 / (FPS / recordingSpeed)
      let sessionStepLoop: NodeJS.Timeout | null = null

      // Start session recording step loop if enabled
      if (useRecording && sessionCanvasRecorderRef.current) {
        sessionStepLoop = setInterval(async () => {
          if (sessionCanvasRecorderRef.current?.status !== RecorderStatus.Recording) return
          try {
            await sessionCanvasRecorderRef.current?.step()
          } catch (error) {
            console.error('Error stepping session recorder', error)
          }
        }, stepInterval)
      }

      // Record frames during countdown if recording is enabled (totalFrames may vary for self-photo)
      const currentTotalFrames = Math.ceil((captureTime * FPS))
      if (useRecording && captureCanvasRecorderRef.current) {
        while (frameCount < currentTotalFrames) {
          if (captureCanvasRecorderRef.current.status !== RecorderStatus.Recording) break

          const currentTime = performance.now()
          const elapsedTime = currentTime - startTime
          const targetFrameTime = (frameCount * 1000) / FPS

          if (elapsedTime >= targetFrameTime) {
            try {
              await captureCanvasRecorderRef.current.step()
              frameCount++
            } catch (error) {
              console.error('Error stepping capture recorder', error)
            }
          }

          // Small delay to prevent CPU overload
          await new Promise((resolve) => setTimeout(resolve, 1))
        }
      }

      // Wait only for the raw capture to complete (but check for recovery during wait)
      await rawCapturePromise
      // Check if recovery started during capture
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Recovery started, stopping this iteration')
        if (sessionStepLoop) {
          clearInterval(sessionStepLoop)
        }
        // Stop any recording that might be in progress
        if (useRecording && captureCanvasRecorderRef.current) {
          try {
            await captureCanvasRecorderRef.current.stop()
          } catch (error) {
            console.error('Error stopping recorder during recovery:', error)
          }
        }
        continue // Skip to next iteration (which will wait for recovery)
      }
      if (sessionStepLoop) {
        clearInterval(sessionStepLoop)
      }

      // Self Photo Mode: zero-delay post-capture path. The photo finishes processing in
      // the background and is appended to the gallery when ready.
      if (isSelfPhotoMode) {
        if (processingPromise) {
          const selfCaptureId = captureId
          pendingProcessing.push(
            processingPromise.then((url) => {
              if (!url) return
              const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
              setSessionState({
                gallery: {
                  capturedItems: [
                    ...prevItems,
                    {
                      id: selfCaptureId,
                      photoUrl: url,
                      recordingUrl: '',
                      timestamp: new Date()
                    }
                  ]
                }
              })
            })
          )
        }

        // Auto-loop ON: immediately start next countdown (no wait, no delay)
        if (selfPhotoAutoLoopRef.current && !stopCaptureRef.current) {
          continue
        }

        // Manual / auto-loop OFF: wait for phone trigger (or End Session / timer expiry)
        if (!stopCaptureRef.current) {
          await new Promise<void>((resolve) => {
            resolveWaitRef.current = (): void => {
              resolveWaitRef.current = null
              resolve()
            }
          })
        }
        continue
      }

      // Classic mode: finalize the shared per-capture recorder now (it must be stopped
      // before the next iteration reuses it), then persist the photo in the background so
      // the next countdown starts without waiting on transforms / hashing / brightness restore.
      let captureUrl: string | undefined = undefined
      if (useRecording && captureCanvasRecorderRef.current) {
        try {
          const captureChunks = await captureCanvasRecorderRef.current.stop()
          // `stop()` resolves undefined when the encoder never produced a frame.
          // `new Blob([undefined])` is not empty — it is the 9-byte ASCII string
          // "undefined", which uploads and plays back as a corrupt file, so the
          // loss only surfaces at the guest's phone. Keep the clip absent instead.
          const byteLength = (captureChunks as ArrayBuffer | undefined)?.byteLength ?? 0
          if (byteLength > 0) {
            const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
            captureUrl = URL.createObjectURL(captureBlob)
          } else {
            logActivity('Capture recorder produced no video data for this shot', {
              recordingType: userPreferences.recordingType,
              remainingCaptures: remainingCapturesRef.current,
              component: 'capture_recorder',
              level: 'error'
            })
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error)
          console.error('Error stopping capture recorder', error)
          logActivity(`Failed to stop capture recorder: ${errorMessage}`, {
            error: errorMessage,
            recordingType: userPreferences.recordingType,
            remainingCaptures: remainingCapturesRef.current,
            component: 'capture_recorder',
            level: 'error'
          })
        }
      }

      if (processingPromise) {
        const classicCaptureId = captureId
        const recordingUrlForCapture = captureUrl
        pendingProcessing.push(
          processingPromise.then((url) => {
            if (!url) return
            if (isRecoveringRef.current || isRecoveringCamera) return
            const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
            setSessionState({
              gallery: {
                capturedItems: [
                  ...prevItems,
                  {
                    id: classicCaptureId,
                    photoUrl: url,
                    ...(applyAfterCapture && { originalPhotoUrl: url }),
                    recordingUrl: recordingUrlForCapture || '',
                    timestamp: new Date()
                  }
                ]
              }
            })
          })
        )
      }

      // Reset countdown for next capture
      if (remainingCapturesRef.current > 1) {
        console.log('Resetting countdown for next capture')
        const nextCaptureTime = config?.settings.captureCountdown || 3
        captureCountdownHook.stop()
        captureCountdownHook.setRemainingSeconds(nextCaptureTime)
        setCaptureCountdown(nextCaptureTime)
        setCountdownKey((prev) => prev + 1)
      } else {
        console.log('Not resetting countdown - no more captures remaining')
      }
    }

    // Drain any in-flight background photo processing so the next page (retake/select/
    // result) sees every captured photo before we navigate.
    if (pendingProcessing.length > 0) {
      await Promise.allSettled(pendingProcessing)
    }

    // Stop and save session recording if enabled
    if (useRecording && sessionCanvasRecorderRef.current) {
      try {
        await stopRecording()
      } catch (error) {
        console.error('Error stopping session recording', error)
      }
    }

    if (stopCaptureRef.current && !isSelfPhotoMode) return

    if (isSelfPhotoMode) {
      if (sessionTimerIntervalRef.current) {
        clearInterval(sessionTimerIntervalRef.current)
        sessionTimerIntervalRef.current = null
      }
      if (window.api?.endSelfPhotoSession) window.api.endSelfPhotoSession()
    }

    logActivity('Capture session completed.', {
      isRetake: userPreferences.retakeCount > 0,
      useRecording: useRecording
    })
    // Upload session state before navigating (fire-and-forget)
    stateUploader.uploadSessionState().catch(() => {})

    // Frame-based mode: navigate to retake, which will then go to filter or select
    if (captureMode === 'frame-based') {
      navigate(`/${projectId}/retake`)
      return
    }

    // Standard mode: navigate to retake first unless self-photo (retake is irrelevant for self-photo)
    const retakeSettings = globalConfig?.pages.retake?.settings
    const retakeEnabled = retakeSettings?.enabled ?? true // Default to enabled if not specified

    // Check if retakes are still allowed (if retake page is enabled). Skip retake entirely for self-photo mode.
    const maxRetakeSession = retakeSettings?.maxRetakeSession
    const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0

    const canRetake =
      !isSelfPhotoMode &&
      retakeEnabled &&
      (maxRetakeSession === undefined ||
        maxRetakeSession === null ||
        retakeSessionCount < maxRetakeSession)

    if (canRetake) {
      navigate(`/${projectId}/retake`)
      return
    }

    // Retake is disabled or not allowed - check filter settings for applyAfterCapture
    const filterEnabled = filterSettings?.enabled

    // If applyAfterCapture is enabled and filter is enabled, navigate to filter
    if (applyAfterCapture && filterEnabled) {
      navigate(`/${projectId}/filter`)
      return
    }

    // Retake disabled and no filter - check Select and Edit pages
    const selectSettings = globalConfig?.pages.select.settings
    const editSettings = globalConfig?.pages.edit.settings
    const selectEnabled = selectSettings?.enabled ?? false
    const editEnabled = editSettings?.enabled ?? false

    // Standard mode always continues to Select, like Retake and Filter do: it is
    // where the strip is built, uploaded and printed, and it auto-processes when
    // disabled. Going straight to Edit/Result here skipped all of that for a
    // guest who used their last retake (B33), and Edit then also missed Select's
    // Checkout-off default of one copy. Self-photo keeps its existing route.
    if (selectEnabled || !isSelfPhotoMode) {
      navigate(`/${projectId}/select`)
    } else if (editEnabled) {
      navigate(`/${projectId}/edit`)
    } else {
      // Both Select and Edit are disabled, go to Result
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    }
  }

  // FIX 3: Helper function to calculate file hash for duplicate detection
  const calculateFileHash = async (blob: Blob): Promise<string> => {
    const arrayBuffer = await blob.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  // Camera recovery callback - called when camera reconnects
  // A page unmount mid-recovery (session reset, navigation) must not leave the global
  // recovery hold set — that would silence the View health monitor and idle-eject for
  // the rest of the app's life.
  useEffect(() => {
    return () => {
    unifiedCameraUtils.setRecovering('primary', false)
    }
  }, [])

  const onCameraReconnected = (): void => {
    console.log('[RECOVERY] Camera reconnected, resuming capture')
    // Clear recovery flags
    isRecoveringRef.current = false
    unifiedCameraUtils.setRecovering('primary', false)
    setIsPreviewPaused(false)
    setIsRecoveringCamera(false)
    setIsCaptureError(false)

    cogoToast.success(
      t('capture.cameraReconnected', 'Camera reconnected successfully. Resuming capture...')
    )

    logActivity('Camera recovery completed, resuming capture', {
      remainingCaptures: remainingCapturesRef.current,
      isRetake: userPreferences.retakeCount > 0
    })

    // Restart countdown from beginning
    const captureTime = config?.settings.captureCountdown || 3
    captureCountdownHook.setRemainingSeconds(captureTime)

    // Note: Capture loop will wait for preview readiness before starting countdown
  }

  // Handle camera recovery when capture fails
  const handleCameraRecovery = async (): Promise<void> => {
    if (isRecoveringCamera) {
      console.log('[RECOVERY] Recovery already in progress')
      return
    }

    // Loop-breaker: count this recovery. It resets to 0 only when a real frame is
    // captured (see onRawCaptured). Once we exceed MAX_CONSECUTIVE_RECOVERIES with no
    // captured frame in between, stop trusting the autonomous fast reconnect and go
    // straight to the operator CameraPrompt (which holds-for-operator on paid sessions).
    const MAX_CONSECUTIVE_RECOVERIES = 3
    consecutiveRecoveryRef.current++
    const forceOperatorPrompt = consecutiveRecoveryRef.current > MAX_CONSECUTIVE_RECOVERIES

    console.log('[RECOVERY] Starting camera recovery process')
    // Reset preview ready flag
    previewReadyAfterRecoveryRef.current = false
    // Set recovery flag in ref immediately so callbacks can check it
    isRecoveringRef.current = true
    unifiedCameraUtils.setRecovering('primary', true)
    setIsRecoveringCamera(true)
    setIsPreviewPaused(true)
    setIsCaptureError(false)
    setCapturing(false) // Stop capturing state
    // Stop countdown immediately and clear onComplete to prevent capture from firing.
    // Keep onTick alive — it already guards with isRecoveringRef and will be needed
    // when the capture loop resumes after recovery.
    captureCountdownHook.stop()
    captureCountdownHook.setOnComplete(null)
    // Reset countdown display
    setCaptureCountdown(initialCaptureCountdown)

    // Don't show recovery message here - it was already shown as "Capture failed" above
    // This keeps the message flow clear: "Capture failed" -> recovery process

    logActivity('Camera recovery process started', {
      remainingCaptures: remainingCapturesRef.current,
      isRetake: userPreferences.retakeCount > 0
    })

    // CRITICAL: Preserve selectedCamera before any disconnect operations
    // This ensures CameraPrompt knows which camera to reconnect
    // Note: selectedCamera should persist across retake sessions, but if it's cleared,
    // we can't recover automatically - user must select camera in settings
    const preservedSelectedCamera = selectedCameraId
    console.log('[RECOVERY] Preserved selectedCamera:', preservedSelectedCamera)

    if (!preservedSelectedCamera) {
      console.error('[RECOVERY] No selectedCamera found, cannot recover')
      cogoToast.error(
        t(
          'capture.recoveryNoCamera',
          'No camera selected. Please select a camera in settings and try again.'
        ),
        { hideAfter: 5 }
      )
      setIsRecoveringCamera(false)
      setIsPreviewPaused(false)
      return
    }

    try {
      // Strategy: Try simple reconnect first (no disconnect). If that fails — or if
      // we've already burned through too many auto-recoveries with no captured frame
      // in between (forceOperatorPrompt) — fall through to the disconnect + operator
      // CameraPrompt path below.
      if (!forceOperatorPrompt) {
        console.log('[RECOVERY] Attempting simple reconnect first (no disconnect)')
        // Show immediate feedback to user
        cogoToast.info(t('capture.recoveryReconnecting', 'Reconnecting camera...'))

        try {
          // Ensure selectedCamera is set before attempting reconnect
          // (it might have been cleared by error handlers)
          const { setSelectedCamera } = useSettingsStore.getState()
          if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
            console.log(
              '[RECOVERY] Restoring selectedCamera before reconnect:',
              preservedSelectedCamera
            )
            setSelectedCamera(preservedSelectedCamera)
          }

          // Try to reconnect without disconnecting first. resolveConnectArgs handles
          // webcam deviceIds, legacy bare productIds, 'detect', and the serial-qualified
          // usb:vid:pid:serial format (same-series twins).
          const { deviceType, params } = resolveConnectArgs(preservedSelectedCamera)

          // Try quick reconnect with timeout (5 seconds max)
          const reconnectPromise = unifiedCameraUtils.tryToConnect(deviceType, params, 'primary')
          const timeoutPromise = new Promise((_resolve, reject) => {
            setTimeout(() => {
              reject(new Error('Reconnection timed out'))
            }, 5000)
          })

          await Promise.race([reconnectPromise, timeoutPromise])

          // tryToConnect RESOLVES even when a stale Camera object is still in the store
          // — the camera branch short-circuits on `if (!currentCamera)` and returns
          // without touching the hardware. So a bare resolve is NOT proof the DSLR is
          // alive; that false success is exactly what looped paid Phoenix sessions
          // (~8.7s recover→resume→re-fail for 8-10 min, never freezing, never returning
          // to Welcome). Verify with a side-effect-free test frame before resuming.
          // capturePreviewForQR never nulls the camera or clears selectedCamera on
          // failure; we probe a couple of times (a just-reconnected camera can be
          // briefly busy) and cap each read so a hung device can't stall recovery. No
          // frame → treat the reconnect as failed and fall through to the operator prompt.
          if (deviceType === 'camera') {
            let testFrame: Blob | undefined
            for (let probe = 0; probe < 2 && !testFrame; probe++) {
              if (probe > 0) await new Promise((r) => setTimeout(r, 300))
              testFrame = await Promise.race([
                unifiedCameraUtils.capturePreviewForQR('primary'),
                new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 1500))
              ])
            }
            if (!testFrame) {
              throw new Error('Reconnect verification failed: camera returned no frame')
            }
          }

          console.log('[RECOVERY] Simple reconnect successful!')
          cogoToast.success(t('capture.recoveryReconnected', 'Camera reconnected!'), { hideAfter: 2 })

          // Success! Resume capture
          onCameraReconnected()
          return
        } catch (reconnectError) {
          console.log(
            '[RECOVERY] Simple reconnect failed, will disconnect and reconnect:',
            reconnectError
          )
          // Continue to disconnect + reconnect flow
        }
      } else {
        console.warn(
          `[RECOVERY] ${consecutiveRecoveryRef.current} consecutive auto-recoveries with no captured frame — skipping fast reconnect, escalating to operator`
        )
        logActivity(
          'Camera recovery escalating to operator (repeated auto-recovery made no progress)',
          {
            consecutiveRecoveries: consecutiveRecoveryRef.current,
            remainingCaptures: remainingCapturesRef.current,
            level: 'error'
          }
        )
      }

      // If simple reconnect failed, disconnect and reconnect
      console.log('[RECOVERY] Disconnecting camera before reconnection')
      cogoToast.info(t('capture.recoveryDisconnecting', 'Disconnecting camera...'), {
        hideAfter: 2
      })

      try {
        await new Promise((resolve) => setTimeout(resolve, 500)) // Reduced from 1000ms
        await unifiedCameraUtils.disconnect('primary', 'capture-recovery')
        console.log('[RECOVERY] Camera disconnected')
      } catch (disconnectError) {
        console.warn('[RECOVERY] Camera disconnect had issues, continuing:', disconnectError)
      }

      // CRITICAL: Restore selectedCamera immediately after disconnect
      // (uniCamUtils error handlers might have cleared it)
      const { setSelectedCamera } = useSettingsStore.getState()
      if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
        console.log(
          '[RECOVERY] Restoring selectedCamera after disconnect:',
          preservedSelectedCamera
        )
        setSelectedCamera(preservedSelectedCamera)
      }

      // Reduced wait time before showing CameraPrompt
      await new Promise((resolve) => setTimeout(resolve, 1000)) // Reduced from 2000ms

      // Trigger CameraPrompt via recovery context
      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        console.log('[RECOVERY] Triggering CameraPrompt for user reconnection')
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        console.error('[RECOVERY] onCameraRecoveryRequest not available in context')
        cogoToast.error(
          t(
            'capture.recoveryContextError',
            'Recovery system error. Please reconnect camera manually.'
          )
        )
        // Fallback: try to reconnect directly
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
      }
    } catch (error) {
      console.error('[RECOVERY] Error during camera recovery:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      cogoToast.error(
        t('capture.recoveryError', 'Camera recovery failed. Please reconnect manually.'),
        { hideAfter: 5 }
      )
      logActivity(`Camera recovery error: ${errorMessage}`, {
        error: errorMessage,
        remainingCaptures: remainingCapturesRef.current,
        level: 'error'
      })
      // Still try to show CameraPrompt as fallback
      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        // If context is not available, reset state
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
      }
    }
  }

  const handleCapture = async (onRawCaptured?: () => void): Promise<string | undefined> => {
    // The loop has already given up; do not start new work or re-arm camera recovery.
    if (captureAbortedRef.current) {
      console.log('[CAPTURE] Capture aborted for this session, skipping')
      return undefined
    }

    // FIX 4: Check capture lock to prevent concurrent captures
    if (captureLockRef.current) {
      console.log('[CAPTURE] Capture already in progress, skipping duplicate')
      return undefined
    }

    captureLockRef.current = true

    try {
      console.log('Starting capture process')

      flashEffect('.flash')
      setCapturing(false)
      setIsCaptureError(false)

      console.log('Capture settings:', {
        useShutterCapture: cameraSettings.useShutterCapture,
        deviceType: useCameraStore.getState().deviceType,
        captureRatio: cameraSettings.captureRatio,
        mirror: cameraSettings.mirror
      })

      if (cameraSettings.useShutterCapture && useCameraStore.getState().deviceType == 'camera') {
        setIsCapturingWithShutter(true)
      }

      // Restore original camera settings before capture if live view brightness was applied
      const cameraKey = userPreferences.selectedCamera as 'primary' | 'secondary'
      if (
        cameraSettings.autoBrightenLiveViewForFlash &&
        cameraSettings.useShutterCapture
      ) {
        try {
          await restoreOriginalSettings(cameraKey)
          console.log('[CAPTURE] Restored original camera settings before capture')
        } catch (error) {
          console.error('[CAPTURE] Failed to restore original settings:', error)
          // Continue with capture even if restoration fails
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 200))
      console.log('Attempting to capture image')
      // Individual photos are always cropped to the app's capture ratio (L2 = getNormalizedCaptureRatio()).
      // Do NOT use effectiveCaptureRatio (which includes framePhotoRatio / L3) or the saved file
      // will be center-cropped to the frame slot aspect (e.g. 1:1) instead of the full L2 crop.
      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyAfterCapture ? (): void => {} : applyFilter,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture,
        onRawCaptured
      )

      setIsCapturingWithShutter(false)

      if (imageData) {
        console.log('Successfully captured image')

        // FIX 5: Calculate file hash to detect duplicates
        const fileHash = await calculateFileHash(imageData)

        // FIX 6: Check if this exact image was already captured
        if (capturedFilesRef.current.has(fileHash)) {
          console.log('[CAPTURE] Duplicate image detected (identical content), skipping upload')
          // The shot was already counted at the raw-capture gate (onRawCaptured); just skip persist.
          return undefined
        }

        // FIX 7: Add hash to tracking set
        capturedFilesRef.current.add(fileHash)

        const imageUrl = URL.createObjectURL(imageData)
        setCapturedImageURL(imageUrl)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          logActivity(`Downloaded photo`, {
            type: 'photo',
            filename: `dreambooth-${id}-${Date.now()}.jpg`
          })
        }

        // Note: the shot is counted at the raw-capture gate (onRawCaptured) in runCaptureLoop,
        // so the next countdown can start before this post-processing finishes.

        // Re-apply live view brightness settings if toggle is still enabled (for next capture)
        if (
          cameraSettings.autoBrightenLiveViewForFlash &&
          cameraSettings.useShutterCapture
        ) {
          try {
            await applyLiveViewBrightness(cameraKey, {
              autoBrightenLiveViewForFlash: cameraSettings.autoBrightenLiveViewForFlash,
              liveViewShutterSpeedOverride: cameraSettings.liveViewShutterSpeedOverride
            })
            console.log('[CAPTURE] Re-applied live view brightness settings after capture')
          } catch (error) {
            console.error('[CAPTURE] Failed to re-apply live view brightness:', error)
            // Continue - preview will handle re-application on next refresh
          }
        }

        return imageUrl
      }

      // No frame this attempt — count it before deciding how to react.
      consecutiveCaptureFailuresRef.current += 1

      // Camera hardware failure.
      //
      // This used to be gated on `cameraSettings.useShutterCapture`, which made it dead
      // code for the failure it was written to catch: the liveview capture path requires
      // useShutterCapture === false (it is passed as `!useShutterCapture` to usePreview)
      // and false is the shipped default. So on a normal booth the predicate was never
      // true, recovery never ran, and the loop-breaker inside handleCameraRecovery never
      // engaged. A missing frame from a camera device is a hardware failure in either
      // capture mode — the mode only changes how we asked for the frame.
      //
      // Shutter mode keeps its original recover-on-first-miss behaviour. The newly
      // enabled liveview path waits for a second consecutive miss, because recovery is a
      // disruptive disconnect/reconnect and liveview already retries internally; a single
      // blip should not tear the camera down. Termination no longer depends on this
      // either way — MAX_CONSECUTIVE_CAPTURE_FAILURES bounds the loop regardless.
      const deviceType = useCameraStore.getState().deviceType
      const isCameraHardwareFailure =
        deviceType === 'camera' &&
        !imageData &&
        (cameraSettings.useShutterCapture || consecutiveCaptureFailuresRef.current >= 2)

      // The real error was recorded (and classified) at the capture boundary — before
      // this, EVERY fleet failure reached the logs as the generic "No image data
      // captured." and focus misses were indistinguishable from dead USB.
      const lastCaptureError = unifiedCameraUtils.getLastCaptureError('primary')
      const captureErrorDetails = {
        error: lastCaptureError?.message ?? 'No image data captured',
        errorKind: lastCaptureError?.kind ?? 'unknown',
        remainingCaptures: remainingCapturesRef.current,
        isRetake: userPreferences.retakeCount > 0
      }

      if (isCameraHardwareFailure) {
        // A focus miss is NOT a hardware failure: the camera is healthy, AF just did
        // not lock (already retried in place at the capture boundary). A reconnect
        // cycle here turns a 1-second refocus into a minutes-long recovery incident.
        // Surface the retry overlay instead — the capture loop takes another shot.
        if (lastCaptureError?.kind === 'focus') {
          console.log('[CAPTURE] Shutter failed on focus; retrying capture without recovery')
          cogoToast.warn(
            t(
              'capture.focusFailed',
              'Camera could not focus. Please step back slightly — retrying...'
            ),
            { hideAfter: 4 }
          )
          logActivity('Capture failed: focus not acquired, retrying in place', {
            ...captureErrorDetails,
            level: 'error'
          })
          setIsCaptureError(true)
          return undefined
        }

        console.log('[CAPTURE] Camera hardware failure detected, triggering recovery')
        // Show capture failure message FIRST - user needs to know what happened
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity('Camera hardware failure detected, starting recovery process.', {
          ...captureErrorDetails,
          level: 'error',
          recovery: true
        })
        // Small delay to let user see the error message, then start recovery
        await new Promise((resolve) => setTimeout(resolve, 500))
        // Trigger camera recovery
        handleCameraRecovery()
        return undefined
      }

      // Log processing failure if no image data (but not camera hardware failure)
      logActivity('Capture processing failed: No image data captured.', {
        ...captureErrorDetails,
        level: 'error'
      })

      setIsCaptureError(true)
      return undefined
    } catch (error) {
      console.error('Capture failed:', error)
      setCapturing(false)
      setIsCapturingWithShutter(false)

      // This attempt produced no frame either.
      consecutiveCaptureFailuresRef.current += 1

      // Camera hardware failure. Same de-gating as the no-imageData path above: the
      // `useShutterCapture` requirement made this unreachable on a default booth.
      // A CameraUnavailableError is definitive (the store has no usable device at all),
      // so it short-circuits the message sniffing.
      const deviceType = useCameraStore.getState().deviceType
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const isCameraHardwareFailure =
        error instanceof CameraUnavailableError ||
        (deviceType === 'camera' &&
          (errorMessage.includes('LIBUSB') ||
            errorMessage.includes('camera') ||
            errorMessage.includes('device') ||
            errorMessage.includes('USB')))

      if (isCameraHardwareFailure) {
        console.log('[CAPTURE] Camera hardware failure detected in catch, triggering recovery')
        // Show capture failure message FIRST - user needs to know what happened
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity(`Camera hardware failure detected: ${errorMessage}`, {
          error: errorMessage,
          remainingCaptures: remainingCapturesRef.current,
          isRetake: userPreferences.retakeCount > 0,
          level: 'error',
          recovery: true
        })
        // Small delay to let user see the error message, then start recovery
        await new Promise((resolve) => setTimeout(resolve, 500))
        // Trigger camera recovery
        handleCameraRecovery()
        return undefined
      }

      // Log processing failure (not camera hardware failure)
      logActivity(`Capture processing failed: ${errorMessage}`, {
        error: errorMessage,
        remainingCaptures: remainingCapturesRef.current,
        isRetake: userPreferences.retakeCount > 0,
        level: 'error'
      })

      setIsCaptureError(true)
      return undefined
    } finally {
      // FIX 9: Release capture lock
      captureLockRef.current = false
    }
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (applyAfterCapture) return
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (selectedFilter?.adjustments) {
      filterUtils.applyAdjustments(ctx, selectedFilter.adjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  // Frame-based mode capture handler
  const handleFrameBasedCapture = async (): Promise<void> => {
    if (captureLockRef.current) {
      console.log('[CAPTURE] Capture already in progress, skipping duplicate')
      return
    }

    captureLockRef.current = true

    try {
      const currentPhotoType = frameBasedUniquePhotoTypesArray[currentPhotoTypeIndex]
      console.log(
        `Starting frame-based capture process for photo type ${currentPhotoType} (${currentPhotoTypeIndex + 1} of ${frameBasedCaptureCount})`
      )

      flashEffect('.flash')
      setCapturing(false)

      // Restore original camera settings before capture if live view brightness was applied
      const cameraKey = userPreferences.selectedCamera as 'primary' | 'secondary'
      if (
        cameraSettings.autoBrightenLiveViewForFlash &&
        cameraSettings.useShutterCapture
      ) {
        try {
          await restoreOriginalSettings(cameraKey)
          console.log('[CAPTURE] Restored original camera settings before frame-based capture')
        } catch (error) {
          console.error('[CAPTURE] Failed to restore original settings:', error)
          // Continue with capture even if restoration fails
        }
      }

      // Same as handleCapture: use getNormalizedCaptureRatio() (L2 only) not effectiveCaptureRatio.
      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        console.log('Successfully captured image')
        const imageUrl = URL.createObjectURL(imageData)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }

        // Store photo by type (one photo can fill multiple slots with same type)
        const newCapturedPhotosByType = new Map(capturedPhotosByType)
        newCapturedPhotosByType.set(currentPhotoType, imageUrl)
        setCapturedPhotosByType(newCapturedPhotosByType)

        // Add to gallery
        const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
        const captureId = `dreambooth-${id}-${Date.now()}-${currentPhotoTypeIndex}`
        setSessionState({
          gallery: {
            capturedItems: [
              ...prevItems,
              {
                id: captureId,
                photoUrl: imageUrl,
                timestamp: new Date()
              }
            ]
          }
        })

        logActivity(
          `Captured photo type ${currentPhotoType} (${currentPhotoTypeIndex + 1} of ${frameBasedCaptureCount})`,
          {
            photoType: currentPhotoType,
            photoTypeIndex: currentPhotoTypeIndex,
            totalPhotos: frameBasedCaptureCount
          }
        )

        // Re-apply live view brightness settings if toggle is still enabled (for next capture)
        if (
          cameraSettings.autoBrightenLiveViewForFlash &&
          cameraSettings.useShutterCapture
        ) {
          try {
            await applyLiveViewBrightness(cameraKey, {
              autoBrightenLiveViewForFlash: cameraSettings.autoBrightenLiveViewForFlash,
              liveViewShutterSpeedOverride: cameraSettings.liveViewShutterSpeedOverride
            })
            console.log('[CAPTURE] Re-applied live view brightness settings after frame-based capture')
          } catch (error) {
            console.error('[CAPTURE] Failed to re-apply live view brightness:', error)
            // Continue - preview will handle re-application on next refresh
          }
        }

        // Move to next photo type or finish
        if (currentPhotoTypeIndex < frameBasedCaptureCount - 1) {
          // Stop capture countdown first
          captureCountdownHook.stop()
          setCapturing(false)

          const nextIndex = currentPhotoTypeIndex + 1
          setCurrentPhotoTypeIndex(nextIndex)

          // Go directly to capture countdown for next photo (no prep countdown)
          setPreparing(false)
          const captureTime = config?.settings.captureCountdown || 3
          captureCountdownHook.stop()
          captureCountdownHook.setRemainingSeconds(captureTime)
          setCaptureCountdown(captureTime)
          setCountdownKey((prev) => prev + 1)
          setCapturing(true)
          // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
          unifiedCameraUtils.prefocus()
          captureCountdownHook.start()
        } else {
          // All photos captured, navigate to retake
          logActivity('All photos captured, navigating to retake (frame-based mode)', {
            totalPhotos: frameBasedCaptureCount
          })
          stateUploader.uploadSessionState().catch(() => {})
          navigate(`/${projectId}/retake`)
        }
      } else {
        logActivity('Frame-based capture processing failed: No image data captured.', {
          level: 'error',
          photoTypeIndex: currentPhotoTypeIndex
        })
        setIsCaptureError(true)
      }
    } catch (error) {
      console.error('Frame-based capture failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Frame-based capture processing failed: ${errorMessage}`, {
        error: errorMessage,
        level: 'error',
        photoTypeIndex: currentPhotoTypeIndex
      })
      setIsCaptureError(true)
    } finally {
      captureLockRef.current = false
    }
  }

  // Frame-based mode start capture session
  const startFrameBasedCaptureSession = (): void => {
    if (isSharedCountdownExpired) {
      cogoToast.error(t('capture.countdownExpired', 'Time has expired'))
      navigate(`/${projectId}/retake`)
      return
    }

    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0 && !isRetakeFromRetakePage
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        try {
          Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ]).catch((error) => {
            // If fetch fails or times out, log but don't block session start
            console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
          })
        } catch (error) {
          console.warn('[CAPTURE] Error setting up fetchSubscriptionAndCredits:', error)
        }
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        stateUploader.uploadSessionState()
      }, 2000)
    }

    prepCountdownHook.stop()
    setPreparing(false)
    const captureTime = config?.settings.captureCountdown || 3
    captureCountdownHook.stop()
    captureCountdownHook.setRemainingSeconds(captureTime)
    setCaptureCountdown(captureTime)
    setCountdownKey((prev) => prev + 1)
    setCapturing(true)
    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
  }

  // Display progress (self-photo shows photo count only)
  const capturedItems = gallery?.capturedItems ?? []
  const captureProgress = isSelfPhotoMode
    ? { current: capturedItems.length, total: capturedItems.length }
    : {
        current:
          userPreferences.retakeCount > 0
            ? userPreferences.retakeCount - remainingCapturesRef.current
            : captureCount - remainingCapturesRef.current,
        total: userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
      }

  const maxRetakeSession = globalConfig?.pages.retake?.settings?.maxRetakeSession
  const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0
  const canRetake =
    maxRetakeSession === undefined ||
    maxRetakeSession === null ||
    retakeSessionCount < maxRetakeSession
  const nextPage = canRetake ? 'retake' : 'select'

  // Frame-based mode UI
  if (captureMode === 'frame-based') {
    // Navigate to frame selection if no frame selected
    if (!selectedFrame) {
      navigate(`/${projectId}/frame`)
      return null
    }
    // Calculate scale for fullscreen frame display
    const screenWidth = layoutCanvasW
    const screenHeight = layoutCanvasH
    const frameWidth = selectedFrame.drawParams.canvasWidth
    const frameHeight = selectedFrame.drawParams.canvasHeight
    const isLandscape = captureLayoutLandscape
    const frameChromePortraitTight = !isLandscape && (captureCompact || captureTiny)
    const frameSessionTimerCompact = captureCompact || captureTiny

    // Scale frame to fit screen while maintaining aspect ratio
    const paddingSize = frameChromePortraitTight
      ? Math.max(screenWidth * 0.02, 10)
      : Math.max(screenWidth * 0.015, 16)
    const topGap = frameChromePortraitTight
      ? Math.max(screenHeight * 0.032, 32)
      : Math.max(screenHeight * 0.05, 80)
    const buttonAreaHeight = frameChromePortraitTight
      ? Math.max(screenHeight * 0.075, 62)
      : Math.max(screenHeight * 0.15, 180)
    const availableWidth = screenWidth - paddingSize * 2
    const availableHeight = screenHeight - paddingSize * 2 - topGap - buttonAreaHeight
    const scale = Math.min(availableWidth / frameWidth, availableHeight / frameHeight)

    const countdownTop = Math.max(screenHeight * 0.02, 32)
    const countdownRight = Math.max(screenWidth * 0.02, 32)
    const countdownFontSize = Math.min(Math.max(screenHeight * 0.022, 22), 34)

    // Get photo ratio from frame
    const getPhotoRatio = (slot: (typeof frameBasedPhotoSlots)[0]): number => {
      return slot.width / slot.height
    }

    return (
      <div className="relative w-full h-full bg-black">
        <div id="flash" className="w-full h-full flash"></div>

        {/* Shared session timer */}
        <div
          className={`absolute z-50 ${frameSessionTimerCompact ? 'right-3 top-3 sm:right-4 sm:top-4' : ''}`}
          style={frameSessionTimerCompact ? undefined : { top: `${countdownTop}px`, right: `${countdownRight}px` }}
        >
          {frameSessionTimerCompact ? (
            <div className="rounded-lg border border-white/35 bg-black/60 px-2.5 py-1.5 text-center shadow-lg backdrop-blur-sm">
              <div className="text-[10px] font-medium uppercase tracking-wide text-white/75">
                {t('capture.timeRemaining', 'Time remaining')}
              </div>
              <div className="font-mono text-base font-semibold tabular-nums leading-none text-white drop-shadow-md">
                {sharedRemainingTime}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-white/90 bg-black/40 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4">
              <div className="mb-1 text-sm text-white/90">{t('capture.timeRemaining', 'Time remaining')}</div>
              <div
                className="font-bold font-mono tabular-nums text-white"
                style={{ fontSize: `${countdownFontSize}px` }}
              >
                {sharedRemainingTime}
              </div>
            </div>
          )}
        </div>

        {isLandscape ? (
          // Landscape Mode: Left big preview + Right frame strip
          <div className="w-full h-full flex">
            {/* Left Area - Big Camera Preview */}
            <div className="w-2/3 relative">
            {/* Capture Countdown Overlay */}
            {capturing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <span
                  key={`countdown-${countdownKey}-${captureCountdown}`}
                  className="text-[200px] text-white animate-countdown drop-shadow-lg"
                >
                  {captureCountdown}
                </span>
              </div>
            )}

              {/* Get Ready Overlay */}
              {preparing && (
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
                  <div className="flex flex-col items-center gap-8 max-w-2xl text-center px-8">
                    <h1 className="text-6xl text-white font-bold drop-shadow-lg">
                      {t('capture.getReady', 'Get Ready!')}
                    </h1>
                    <button
                      onClick={startFrameBasedCaptureSession}
                      className="text-2xl hover:opacity-80 transition-opacity bg-white px-8 py-4 rounded-lg shadow-lg text-gray-800 font-semibold"
                    >
                      {t('capture.startNow', 'Start Now')}
                    </button>
                  </div>
                </div>
              )}

              {/* Capture Error Message */}
              {isCaptureError && (
                <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
                  <div className="text-center">
                    <h2 className="text-4xl font-bold text-red-500 mb-4">
                      {t('capture.captureFailed', 'Capture Failed')}
                    </h2>
                    <p className="text-xl text-white/80 mb-6">
                      {t('capture.stayStillCapturing', 'Please stay still when capturing')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCaptureError(false)
                        startFrameBasedCaptureSession()
                      }}
                      className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer px-6 py-3 text-white"
                    >
                      {t('common.retry', 'Retry')}
                    </button>
                  </div>
                </div>
              )}

              {/* Camera Preview */}
              <div className="w-full h-full">
                <Preview
                  adjustments={selectedFilter?.adjustments}
                  ratio={previewHighlightRatio}
                  cameraRatio={getNormalizedCaptureRatio()}
                  cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  filter={selectedFilter?._id}
                  postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                  onCanvasReady={setCanvasDimensions}
                  onRecordingCanvasReady={onRecordingCanvasReady}
                />
              </div>
            </div>

            {/* Right Area - Frame Strip */}
            <div className="w-1/3 relative bg-black">
              {/* Frame Strip */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ padding: `${paddingSize}px` }}
              >
                <div
                  className="relative flex items-center justify-center"
                  style={{
                    width: `${frameWidth * scale * 0.95}px`,
                    height: `${frameHeight * scale * 0.95}px`
                  }}
                >
                  {/* Frame Background Image */}
                  <img
                    src={selectedFrame.thumbnail}
                    alt="Frame"
                    className="absolute inset-0 w-full h-full object-contain"
                    crossOrigin="anonymous"
                  />

                  {/* Photo Placeholders with Preview */}
                  {frameBasedPhotoSlots.map((slot, index) => {
                    const currentPhotoType = frameBasedUniquePhotoTypesArray[currentPhotoTypeIndex]
                    const isCurrentSlot = slot.type === currentPhotoType
                    const capturedPhotoUrl = capturedPhotosByType.get(slot.type)

                    return (
                      <div
                        key={`${slot.type}-${index}`}
                        className="absolute overflow-hidden"
                        style={{
                          left: `${slot.left * scale * 0.95}px`,
                          top: `${slot.top * scale * 0.95}px`,
                          width: `${slot.width * scale * 0.95}px`,
                          height: `${slot.height * scale * 0.95}px`
                        }}
                      >
                        {capturedPhotoUrl ? (
                          <img
                            src={capturedPhotoUrl}
                            alt={`Photo ${slot.type}`}
                            className="w-full h-full object-cover"
                            crossOrigin="anonymous"
                          />
                        ) : isCurrentSlot ? (
                          <div className="w-full h-full relative overflow-hidden">
                            <Preview
                              adjustments={selectedFilter?.adjustments}
                              ratio={getPhotoRatio(slot)}
                              cameraRatio={getNormalizedCaptureRatio()}
                              cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                              filter={selectedFilter?._id}
                              postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                              onCanvasReady={setCanvasDimensions}
                              fillContainer={true}
                            />
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <span className="text-gray-400 text-2xl">
                              {frameBasedUniquePhotoTypesArray.indexOf(slot.type) + 1}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Progress Counter */}
              <div
                className="absolute z-10"
                style={{
                  bottom: `${Math.max(screenHeight * 0.02, 32)}px`,
                  right: `${Math.max(screenWidth * 0.03, 48)}px`
                }}
              >
                <div className="bg-black border-2 border-white/20 p-4 text-center rounded-lg">
                  <div className="text-sm text-white/80 mb-1">Progress</div>
                  <div
                    className="font-bold text-white"
                    style={{ fontSize: `${Math.max(screenHeight * 0.02, 24)}px` }}
                  >
                    {Math.min(currentPhotoTypeIndex + 1, frameBasedCaptureCount)}/
                    {frameBasedCaptureCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Portrait Mode: Centered frame strip only
          <div
            className="relative w-full h-full flex flex-col overflow-hidden"
            style={{ padding: `${paddingSize}px` }}
          >

            {/* Frame Preview Area */}
            <div
              className="flex-1 min-h-0 relative flex items-center justify-center"
              style={{ paddingTop: `${topGap}px` }}
            >
              <div
                className="absolute flex items-center justify-center"
                style={{
                  width: `${frameWidth * scale}px`,
                  height: `${frameHeight * scale}px`
                }}
              >
                {/* Frame Background Image */}
                <img
                  src={selectedFrame.thumbnail}
                  alt="Frame"
                  className="absolute inset-0 w-full h-full object-contain"
                  crossOrigin="anonymous"
                />

                {/* Photo Placeholders with Preview */}
                {frameBasedPhotoSlots.map((slot, index) => {
                  const currentPhotoType = frameBasedUniquePhotoTypesArray[currentPhotoTypeIndex]
                  const isCurrentSlot = slot.type === currentPhotoType
                  const capturedPhotoUrl = capturedPhotosByType.get(slot.type)

                  return (
                    <div
                      key={`${slot.type}-${index}`}
                      className="absolute overflow-hidden"
                      style={{
                        left: `${slot.left * scale}px`,
                        top: `${slot.top * scale}px`,
                        width: `${slot.width * scale}px`,
                        height: `${slot.height * scale}px`
                      }}
                    >
                      {capturedPhotoUrl ? (
                        <img
                          src={capturedPhotoUrl}
                          alt={`Photo ${slot.type}`}
                          className="w-full h-full object-cover"
                          crossOrigin="anonymous"
                        />
                      ) : isCurrentSlot ? (
                        <div className="w-full h-full relative overflow-hidden">
                          <Preview
                            adjustments={selectedFilter?.adjustments}
                            ratio={getPhotoRatio(slot)}
                            cameraRatio={getNormalizedCaptureRatio()}
                            cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                            filter={selectedFilter?._id}
                            postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                            onCanvasReady={setCanvasDimensions}
                            onRecordingCanvasReady={onRecordingCanvasReady}
                            fillContainer={true}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                          <span className="text-gray-400 text-2xl">
                            {frameBasedUniquePhotoTypesArray.indexOf(slot.type) + 1}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

        {/* Prep Countdown Overlay */}
        {preparing && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
            <div className="flex flex-col items-center gap-8 max-w-2xl text-center px-8">
              <h1 className="text-[90px] text-center bg-gradient-to-r text-white bg-clip-text text-transparent font-bold">
                {t('capture.getReady', 'Get Ready!')}
              </h1>
              <p
                className="text-white/80"
                style={{ fontSize: `${Math.max(screenHeight * 0.04, 48)}px` }}
              >
                {t('capture.sessionStarting', 'Session will start in')}{' '}
                <span
                  className="font-mono tabular-nums font-bold text-white"
                  style={{ fontSize: `${Math.max(screenHeight * 0.05, 60)}px` }}
                >
                  {prepCountdown}
                </span>
              </p>
              <button
                type="button"
                onClick={startFrameBasedCaptureSession}
                disabled={!canvasDimensions}
                className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap px-8 py-4"
                style={{
                  fontSize: `${Math.max(screenHeight * 0.04, 48)}px`,
                  color: '#ffffff'
                }}
              >
                {t('capture.startNow', 'Start Now')}
              </button>
              <span className="text-white/60 text-lg">
                {t('capture.orWaitCountdown', 'or wait for the countdown')}
              </span>
            </div>
          </div>
        )}

        {/* Capture Countdown Overlay */}
        {capturing && (
          <div className="absolute inset-0 z-[4] flex items-center justify-center">
            <span
              key={`countdown-${countdownKey}-${captureCountdown}`}
              className="text-white animate-countdown"
              style={{
                fontSize: `${frameChromePortraitTight ? Math.min(Math.max(screenHeight * 0.12, 64), 120) : Math.max(screenHeight * 0.18, 200)}px`
              }}
            >
              {captureCountdown}
            </span>
          </div>
        )}

        {/* Capture Error Message */}
        {isCaptureError && (
          <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-red-500 mb-4">
                {t('capture.captureFailed', 'Capture Failed')}
              </h2>
              <p className="text-xl text-white/80 mb-6">
                {t('capture.stayStillCapturing', 'Please stay still when capturing')}
              </p>
              <button
                type="button"
                onClick={() => {
                  setIsCaptureError(false)
                  startFrameBasedCaptureSession()
                }}
                className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer px-6 py-3"
                style={{
                  fontSize: `${Math.max(screenHeight * 0.018, 18)}px`,
                  color: '#ffffff'
                }}
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          </div>
        )}

            {/* Progress Indicator */}
            <div className="flex-shrink-0 relative" style={{ height: `${buttonAreaHeight}px` }}>
              <div
                className="absolute z-10"
                style={{
                  bottom: `${frameSessionTimerCompact ? 12 : Math.max(screenHeight * 0.02, 32)}px`,
                  right: `${frameSessionTimerCompact ? 12 : Math.max(screenWidth * 0.03, 48)}px`
                }}
              >
                <div
                  className={`rounded-lg border border-white/25 bg-black/70 text-center shadow-md backdrop-blur-sm ${
                    frameSessionTimerCompact ? 'px-2 py-1.5' : 'border-2 border-white/20 p-4'
                  }`}
                >
                  <div
                    className={`text-white/80 ${frameSessionTimerCompact ? 'mb-0 text-[10px] uppercase tracking-wide' : 'mb-1 text-sm'}`}
                  >
                    Progress
                  </div>
                  <div
                    className={`font-bold tabular-nums text-white ${frameSessionTimerCompact ? 'text-sm' : ''}`}
                    style={
                      frameSessionTimerCompact
                        ? undefined
                        : { fontSize: `${Math.max(screenHeight * 0.02, 24)}px` }
                    }
                  >
                    {Math.min(currentPhotoTypeIndex + 1, frameBasedCaptureCount)}/
                    {frameBasedCaptureCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Standard mode UI (existing)
  return (
    <div className="relative w-full h-full bg-carbon">
      <div id="flash" className="w-full h-full flash"></div>

      {/* Shutter capture message */}
      {isCapturingWithShutter && (
        <div className="absolute inset-0 bg-white/50 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-black mb-4">
              📸 {t('capture.lookAtCamera', 'Look at the Camera')}
            </h2>
            <p className="text-xl text-black/80">
              {t('capture.stayStill', 'Please stay still while we capture your photo...')}
            </p>
          </div>
        </div>
      )}

      {/* Terminal capture failure: the loop gave up and nothing was captured.
          Replaces the old behaviour where the "Retrying in a moment..." overlay below
          stayed up indefinitely with no button, no timeout and no way out — the screen
          15 paying customers were left staring at. This one states the situation and
          hands control to an operator. */}
      {captureHardFailure && (
        <div className="absolute inset-0 bg-black/90 z-[6] flex items-center justify-center">
          <div className="text-center max-w-2xl px-6">
            <h2 className="text-4xl font-bold text-red-500 mb-4">
              {t('capture.hardFailureTitle', 'Photos Could Not Be Taken')}
            </h2>
            <p className="text-xl text-white/80 mb-4">
              {t(
                'capture.hardFailureBody',
                'We could not take any photos. Your session and payment are saved.'
              )}
            </p>
            <p className="text-lg text-white/60 mb-8">
              {t('capture.hardFailureAction', 'Please ask an operator for assistance.')}
            </p>
            <Button
              text={t('capture.hardFailureExit', 'Operator: end session')}
              onClick={() => {
                void sessionReset('capture_hard_failure_operator_exit')
              }}
            />
          </div>
        </div>
      )}

      {/* Capture error message - only show for non-connection errors */}
      {isCaptureError && !captureHardFailure && (
        <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-red-500 mb-4">
              {t('capture.captureFailed', 'Capture Failed')}
            </h2>
            <p className="text-xl text-white/80 mb-6">
              {t('capture.stayStillCapturing', 'Please stay still when capturing')}
            </p>
            <p className="text-lg text-white/60">
              {t('capture.retryingMoment', 'Retrying in a moment...')}
            </p>
          </div>
        </div>
      )}

      {/* Self Photo Mode: left controls + right gallery */}
      {isSelfPhotoMode && (
        <>
          <div
            className={`absolute bottom-0 left-0 top-0 z-10 flex flex-col overflow-y-auto bg-black/40 p-2 sm:p-4 ${
              captureTiny ? 'gap-2' : 'gap-4'
            } ${
              captureTiny
                ? capturePortrait
                  ? 'min-w-[100px] w-[min(36%,200px)]'
                  : 'min-w-[96px] w-[min(26%,160px)]'
                : captureCompact
                  ? capturePortrait
                    ? 'min-w-[140px] w-[min(42%,240px)]'
                    : 'min-w-[120px] w-[min(28%,180px)]'
                  : capturePortrait
                    ? 'min-w-[200px] w-[min(34%,320px)]'
                    : 'w-[220px]'
            }`}
          >
            <div className="flex-1 min-h-0" />
            <button
              type="button"
              onClick={() => {
                phoneTriggerRequestedRef.current = true
                const resolve = resolveWaitRef.current
                if (resolve) {
                  resolveWaitRef.current = null
                  resolve()
                }
              }}
              title={t('capture.selfPhotoTriggerNow', 'Trigger capture now')}
              aria-label={t('capture.selfPhotoTriggerNow', 'Trigger capture now')}
              className={`${captureTiny ? 'w-12 h-12' : 'w-14 h-14'} rounded-full bg-white/90 hover:bg-white text-black shadow-lg flex items-center justify-center shrink-0 mx-auto transition-colors`}
            >
              <svg className={captureTiny ? 'w-5 h-5' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="12" r="6" />
              </svg>
            </button>
            {selfPhotoQrDataUrl && (
              <div className="flex flex-col items-center gap-1 bg-black/60 rounded-lg p-1.5 sm:p-2 shrink-0">
                <img
                  src={selfPhotoQrDataUrl}
                  alt={t('capture.selfPhotoQRAlt', 'Trigger from phone')}
                  className={captureTiny ? 'w-16 h-16' : 'w-20 h-20'}
                />
                <span className="text-white/90 text-xs text-center">
                  {t('capture.selfPhotoScanQR', 'Scan to trigger from phone')}
                </span>
                {selfPhotoTriggerUrlDisplay && (
                  <span className="text-white/70 text-[10px] text-center break-all font-mono mt-1">
                    {selfPhotoTriggerUrlDisplay}
                  </span>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-1 shrink-0">
              {([0, 3, 6, 10] as const).map((sec) => (
                <button
                  key={sec}
                  type="button"
                  onClick={() => {
                    setSelfPhotoCountdownSeconds(sec)
                    selfPhotoCountdownSecondsRef.current = sec
                    if (sec === 0) {
                      setSelfPhotoAutoLoop(false)
                      selfPhotoAutoLoopRef.current = false
                    }
                    if (window.api?.updateSelfPhotoSettings) {
                      window.api.updateSelfPhotoSettings(sec === 0 ? { countdownSeconds: 0, autoLoop: false } : { countdownSeconds: sec })
                    }
                  }}
                  className={`${
                    captureTiny ? 'px-2 py-1.5 text-xs min-w-[40px]' : 'px-3 py-2 text-sm min-w-[44px]'
                  } rounded font-medium transition-colors flex items-center justify-center ${
                    selfPhotoCountdownSeconds === sec
                      ? 'bg-white text-black'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {sec === 0 ? (
                    <span title={t('capture.selfPhotoManualOnly', 'Manual')} aria-label={t('capture.selfPhotoManualOnly', 'Manual')}>
                      &#x1F6AB;
                    </span>
                  ) : (
                    `${sec}s`
                  )}
                </button>
              ))}
            </div>
            {selfPhotoCountdownSeconds !== 0 && (
              <div className="flex items-center gap-2 bg-black/60 rounded-lg px-3 py-2 shrink-0">
                <span className="text-white/90 text-sm">
                  {t('capture.selfPhotoAutoLoop', 'Auto')}
                </span>
                <button
                  type="button"
                  title={selfPhotoAutoLoop ? t('capture.selfPhotoAutoLoopOn', 'Auto capture on') : t('capture.selfPhotoAutoLoopOff', 'Auto capture off')}
                  onClick={() => {
                    const next = !selfPhotoAutoLoop
                    setSelfPhotoAutoLoop(next)
                    selfPhotoAutoLoopRef.current = next
                    if (window.api?.updateSelfPhotoSettings) {
                      window.api.updateSelfPhotoSettings({ autoLoop: next })
                    }
                    if (next) {
                      const resolve = resolveWaitRef.current
                      if (resolve) {
                        resolveWaitRef.current = null
                        resolve()
                      }
                    }
                  }}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    selfPhotoAutoLoop ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      selfPhotoAutoLoop ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            )}
            <div className="bg-black/60 rounded-lg px-4 py-2 text-center shrink-0">
              <span className="text-white/70 text-xs block">
                {t('capture.selfPhotoSessionTimer', 'Session time')}
              </span>
              <span className="text-xl font-mono tabular-nums text-white">
                {Math.floor(sessionTimerSeconds / 60)}:{(sessionTimerSeconds % 60).toString().padStart(2, '0')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (sessionTimerSeconds > 0) {
                  setShowEndSessionModal(true)
                } else {
                  stopCaptureRef.current = true
                  const resolve = resolveWaitRef.current
                  if (resolve) {
                    resolveWaitRef.current = null
                    resolve()
                  }
                }
              }}
              className={`bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg transition-colors shrink-0 ${
                captureTiny
                  ? 'text-sm px-3 py-2'
                  : captureCompact
                    ? 'text-base px-4 py-3'
                    : 'text-lg px-6 py-4'
              }`}
            >
              {t('capture.selfPhotoEndSessionBtn', 'End Session')}
            </button>
          </div>
      {showEndSessionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className={`bg-white rounded-lg max-w-lg w-full mx-4 ${captureTiny ? 'p-4' : 'p-8'}`}>
            <h2 className={`font-bold mb-4 ${captureTiny ? 'text-lg' : 'text-2xl'}`}>
              {t('capture.selfPhotoEndSessionConfirmTitle', 'End session early?')}
            </h2>
            <p className={`mb-8 text-gray-600 ${captureTiny ? 'text-sm mb-4' : ''}`}>
              {t('capture.selfPhotoEndSessionConfirmMessage', 'You still have {{time}} remaining. Are you sure?', {
                time: `${Math.floor(sessionTimerSeconds / 60)}:${(sessionTimerSeconds % 60).toString().padStart(2, '0')}`
              })}
            </p>
            <div className="flex justify-end gap-3 flex-wrap">
              <Button
                text={t('capture.selfPhotoEndSessionCancel', 'Cancel')}
                onClick={() => setShowEndSessionModal(false)}
                variant="secondary"
                className="!px-6 !py-2 !text-base whitespace-nowrap"
              >
                <span className="whitespace-nowrap">{t('capture.selfPhotoEndSessionCancel', 'Cancel')}</span>
              </Button>
              <Button
                text={t('capture.selfPhotoEndSessionConfirm', 'Yes, end session')}
                onClick={() => {
                  setShowEndSessionModal(false)
                  stopCaptureRef.current = true
                  const resolve = resolveWaitRef.current
                  if (resolve) {
                    resolveWaitRef.current = null
                    resolve()
                  }
                }}
                variant="primary"
                className="!px-6 !py-2 !text-base whitespace-nowrap bg-red-600 hover:bg-red-700"
              >
                <span className="whitespace-nowrap">{t('capture.selfPhotoEndSessionConfirm', 'Yes, end session')}</span>
              </Button>
            </div>
          </div>
        </div>
      )}
          <div
            className={`absolute bottom-0 right-0 top-0 z-10 flex flex-col bg-black/60 ${
              captureTiny
                ? capturePortrait
                  ? 'min-w-[88px] w-[min(20%,160px)]'
                  : 'min-w-[88px] w-[min(22%,140px)]'
                : captureCompact
                  ? capturePortrait
                    ? 'min-w-[100px] w-[min(22%,180px)]'
                    : 'min-w-[100px] w-[min(24%,160px)]'
                  : capturePortrait
                    ? 'min-w-[140px] w-[min(28%,260px)]'
                    : 'w-[220px]'
            }`}
          >
            <div className="p-2 border-b border-white/20 shrink-0">
              <span className="text-white/90 text-sm font-medium">
                {t('capture.selfPhotoPhotoCount', '{{count}} photos', { count: capturedItems.length })}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col gap-2 p-2">
              {[...capturedItems].reverse().map((item) => (
                <div key={item.id} className="aspect-square w-full rounded overflow-hidden border border-white/20 shrink-0 relative group">
                  <img
                    src={item.photoUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSessionState({
                        gallery: {
                          capturedItems: capturedItems.filter((i) => i.id !== item.id)
                        }
                      })
                    }}
                    title={t('capture.removePhoto', 'Remove photo')}
                    aria-label={t('capture.removePhoto', 'Remove photo')}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center text-sm font-bold transition-colors"
                  >
                    &#215;
                  </button>
                </div>
              ))}
              {capturedItems.length === 0 && (
                <span className="text-white/50 text-sm py-8 text-center">
                  {t('capture.noPhotoYet', 'No photo yet')}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      <div
        className={`absolute z-10 flex flex-col items-center gap-4 ${captureCompact ? 'bottom-8 right-4' : 'bottom-24 right-12'}`}
      >
        {!isSelfPhotoMode && (
          <>
            <div className="bg-white/10 p-2 rounded-lg">
              {capturedImageURL ? (
                <div style={containerStyle}>
                  <img
                    src={capturedImageURL}
                    alt={t('capture.lastCaptured', 'Last captured')}
                    style={getImageStyle()}
                    crossOrigin="anonymous"
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: captureThumbPx * displayRatio,
                    height: captureThumbPx
                  }}
                  className="flex items-center justify-center bg-black/30"
                >
                  <span className={`text-white/50 ${captureCompact ? 'text-sm' : 'text-xl'}`}>
                    {t('capture.noPhotoYet', 'No photo yet')}
                  </span>
                </div>
              )}
            </div>
            <span className={`font-bold text-white ${captureCompact ? 'text-2xl' : 'text-5xl'}`}>
              {captureProgress.current}/{captureProgress.total}
            </span>
          </>
        )}
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
        {preparing && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
            <div
              className={`flex max-w-2xl flex-col items-center text-center px-4 sm:px-8 ${
                captureTiny ? 'gap-3' : captureCompact ? 'gap-4' : 'gap-8'
              }`}
            >
              <h1
                className={`bg-gradient-to-r from-white to-white/90 bg-clip-text text-center font-bold text-transparent leading-tight ${
                  captureTiny ? 'text-[32px]' : captureCompact ? 'text-[48px]' : 'text-[90px]'
                }`}
              >
                {t('capture.getReady', 'Get Ready!')}
              </h1>

              <p
                className={
                  captureTiny ? 'text-base text-white/80' : captureCompact ? 'text-lg text-white/80' : 'text-2xl text-white/80'
                }
              >
                {t('capture.sessionStarting', 'Session will start in')}{' '}
                <span
                  className={`font-mono tabular-nums font-bold text-white ${
                    captureTiny ? 'text-xl' : captureCompact ? 'text-2xl' : 'text-4xl'
                  }`}
                >
                  {prepCountdown}
                </span>
              </p>

              <div className="flex flex-col items-center gap-3">
                <Button
                  onClick={startCaptureSession}
                  text={t('capture.startNow', 'Start Now')}
                  data-nav-to={nextPage}
                  className={`rounded-2xl font-bold transition-all duration-300 ${
                    captureTiny
                      ? 'w-[160px] py-2.5 text-base'
                      : captureCompact
                        ? 'w-[180px] py-3 text-xl'
                        : 'w-[300px] py-6 text-3xl'
                  } ${!canvasDimensions ? 'cursor-not-allowed opacity-50' : 'hover:scale-105'}`}
                  disabled={!canvasDimensions}
                />
                <span className={`text-white/60 ${captureTiny ? 'text-sm' : 'text-lg'}`}>
                  {t('capture.orWaitCountdown', 'or wait for the countdown')}
                </span>
              </div>
            </div>
          </div>
        )}

        {capturing && (
          <div className="absolute inset-0 z-[4] flex items-center justify-center">
            <span
              key={`countdown-${countdownKey}-${captureCountdown}`}
              className={`text-white animate-countdown drop-shadow-lg ${
                captureTiny ? 'text-[72px]' : captureCompact ? 'text-[120px]' : 'text-[348px]'
              }`}
            >
              {captureCountdown}
            </span>
          </div>
        )}

        <Preview
          key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${effectiveCaptureRatio}-${previewHighlightRatio ?? 'auto'}`}
          adjustments={selectedFilter?.adjustments}
          ratio={previewHighlightRatio}
          cameraRatio={getNormalizedCaptureRatio()}
          onCanvasReady={setCanvasDimensions}
          onRecordingCanvasReady={onRecordingCanvasReady}
          cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
          filter={selectedFilter?._id}
          postLUTAdjustments={selectedFilter?.postLUTAdjustments}
          isPaused={isPreviewPaused}
        />
      </div>

      {userPreferences.useRecording && !preparing && (
        <div
          className={`absolute z-[10] flex items-center ${
            captureTiny ? 'left-2 top-6 gap-1.5' : captureCompact ? 'left-4 top-8 gap-2' : 'left-12 top-12 gap-3'
          }`}
        >
          <div className={`flex items-center ${captureTiny ? 'gap-1' : captureCompact ? 'gap-1.5' : 'gap-2'}`}>
            <div
              className={`rounded-full bg-red-500 animate-pulse ${
                captureTiny ? 'h-2 w-2' : captureCompact ? 'h-2.5 w-2.5' : 'h-4 w-4'
              }`}
            />
            <span
              className={`font-medium text-red-500 ${captureTiny ? 'text-xs' : captureCompact ? 'text-sm' : 'text-[24px]'}`}
            >
              {t('capture.recording', 'REC')}
            </span>
          </div>
        </div>
      )}

    </div>
  )
}

const CapturePage: React.FC = () => {
  const { config: globalConfig } = useConfigStore()
  const captureMode = globalConfig?.pages.capture?.settings?.mode || 'standard'
  if (captureMode === 'frame-based') {
    return <FrameBasedCapture />
  }
  return <StandardCapturePage />
}

export default CapturePage
